/**
 * AgentsDashboardView -- Panel grafico estado live de los Argys del ecosistema.
 *
 * Renderiza un grid de cards, una por agente conocido, con:
 *   - Avatar emoji narrativo (Squanchy, Mr. Poopybutthole, Inquisidor, ...)
 *   - Dot color segun estado (WIP azul, DONE verde, BLOCKED rojo, IDLE gris, OFF gris oscuro)
 *   - Heartbeat: hace cuanto fue el ultimo evento ("hace 3 min", "hace 2 h")
 *   - Mision activa (mission_code del ultimo evento de trabajo)
 *   - Click en card: drill-down panel inferior con ultimos 5 eventos del agente
 *
 * Polling cada 30s con setInterval. Cleanup via MutationObserver al destruir vista.
 *
 * @module presentation/views/AgentsDashboardView
 */

import { AgentsDashboardApiClient, KNOWN_AGENTS } from '../../infrastructure/api/AgentsDashboardApiClient.js';
import { MissionApiClient } from '../../infrastructure/api/MissionApiClient.js';
import { TaskApiClient } from '../../infrastructure/api/TaskApiClient.js';
import { BitacoraApiClient } from '../../infrastructure/api/BitacoraApiClient.js';
import { NxsApiClient } from '../../infrastructure/api/NxsApiClient.js';
import { AGENT_REGISTRY, EXECUTOR_CODES, normalizeAgentCode, isLegacyAgent, ORIGENES_NO_AGENTE } from '../../infrastructure/api/AgentRegistry.js';

const POLL_INTERVAL_MS = 30000;

// Deuda 4: era una copia del escape; ahora es el compartido.
import { esc as escHtml } from '../shared/texto.js';
import { regresoHtml } from '../shared/regreso.js';
import { formatearFecha } from '../shared/fechaColombiana.js';

// Agentes que ejecutan misiones (review queue). DERIVADO del registro unico
// (PANEL-AGENTS-DASHBOARD-V1-001 B: drift kill).
const EXECUTOR_AGENTS = new Set(EXECUTOR_CODES);

// ===================== KPIs cuantitativos (SPEC KPI v1 — ASSIGN #2695) =====================
// Todo client-side sobre APIs existentes (missions/tasks/bitacora/nxs). Sin backend nuevo.

// ===========================================================================
// EL PROGRESO POR HITO, Y POR QUE TODAVIA HAY UNA LISTA A MANO AQUI
// ===========================================================================
//
// Argy-Q lo audito (TABLERO-AUDIT-T1, #11585) y el veredicto era duro: este
// indicador PESA BIEN por NXS, pero le pregunta a una lista escrita a mano en
// este archivo. Medido de nuevo el 7-sep contra la base, y sigue igual:
//
//     95 misiones activas · 30 en la lista · 65 FUERA
//     233.800 NXS en juego · 159.200 fuera de la cuenta = 68,1%
//
// O sea que el numero que Josse mira habla de un tercio del trabajo y no lo
// dice. Eso es lo que se arregla: no se puede calcular todavia desde la base,
// pero SI se puede dejar de mentir en silencio.
//
// POR QUE NO SE CALCULA YA DESDE LA BASE. El dato existe -- nexo_missions tiene
// phase_code y phase_order -- pero NO VIAJA: PostgresMissionRepository lista sus
// columnas una por una y ninguna de las dos esta, y MissionDtos tampoco las
// declara. Leido en NexoAPI origin/main. Pedido a la API en bitacora.
//
// EL DIA QUE LLEGUE, ESTO SE ENCIENDE SOLO: buildHitoProgress mira primero si
// las misiones traen phaseCode y, si lo traen, calcula desde ahi -- con las que
// no tengan hito agrupadas como "sin hito", que es lo que pidio la tarea: no
// desaparecen. Sin re-deploy y sin que nadie tenga que acordarse.
//
// La lista de abajo es el plan B mientras tanto. Sale de la auditoria 68/68 de
// Argy-Q (bitacora #2699, corte 2026-07-06), y H2 quedo sin misiones mapeadas.
const HITO_MISSIONS = {
  H1: ['NEGOCIOS-ONBOARD-CHECKLIST-V1-001', 'SYNC-RECREAR-SAFE-V1-001', 'DESKTOP-V15-MIGRATION-CONSISTENCY-V1-001', 'USUARIOS-IDENTIDAD-SYNC-V1-001', 'WEB-TOKEN-PER-BUSINESS-V1-001', 'DESKTOP-SYNC-CEDULA-ACCESS-AUTO-V1-001', 'DATA-PARITY-V1-V15-PANEL-V1-001', 'NEXO-API-SYNC-EXPAND-001'],
  H3: ['PANEL-COMPRAS-INVENTARIO-V1-001', 'DB-SCHEMA-MIGRATION-COMPRAS-CLEAN-V1-001', 'COMPRAS-EGRESO-ITEMS-HORA-PAYLOAD-V1-001', 'COMPRAS-RUBROS-CATALOG-PLUS-B22-V1-001', 'COMPRAS-V1-EGRESOS-ARQUEO-INTEGRATION-V1-001', 'COMPRAS-EGRESO-RUBRO-MATCH-V1-001', 'DESKTOP-INSUMOS-SYNC-DOWNLOAD-V1-001', 'DESKTOP-INSUMOS-CONFIG-BIDIR-V1-001', 'PANEL-FACTURAS-INSUMO-MATCH-FIX-V1-001', 'PHASE-2-EMBUDO-FINANCIERO-V1-001'],
  H4: ['RECONTEO-INLINE-F1-4-001', 'VERIFICACION-CONFIG-ASIGNACION-V1-001', 'INVENTARIO-POR-ZONA-V15-001', 'INVENTARIO-PORCIONADO-ALERTAS-V1-001', 'DESKTOP-V15-INVENTARIO-LOGICA-FUNCIONAL-V1-001', 'PANEL-INVENTARIO-CONFIG-PERSIST-BD-V1-001', 'PANEL-MOVIMIENTO-FORM-INSUMO-CANTIDAD-V1-001', 'DESKTOP-PANEL-CONFIG-PARITY-V1-001'],
  H5: ['NOTIFICATIONS-ALERTS-V1-001', 'DESKTOP-FINANCIERO-V15-INTERFAZ-NUEVA-001'],
  H6: ['PANEL-OPERATIONS-001'],
  H7: ['PANEL-ASISTENCIA-VALIDATION-V2-001', 'HORARIOS-AUDIT-001', 'NOMINA-AUDIT-V1-001'],
  H8: ['DESKTOP-FINANCIERO-V15-ARQUEO-001', 'ARQUEO-SYNC-ALIGN-V1-001', 'CONCILIACION-BANCARIA-V1-001'],
  HT: ['DESKTOP-CAPACITACION-FORM-V15-001', 'ARGEMIRO-ONBOARDING-001', 'DESKTOP-V15-AUDIT-MANUAL-V1-001', 'ARGEMIRO-CAPABILITIES-EXPAND-001']
};

const DAY_MS = 24 * 3600 * 1000;
const ACTIVE_STATUSES = new Set(['active', 'in_progress']);

function missionStatus(m) { return String(m?.status || '').toLowerCase(); }
function missionReward(m) { return Number(m?.rewardNexos ?? m?.reward_nexos ?? 0) || 0; }

// done/total de tareas de una mision contra el indice de status (tareas fuera de
// la pagina cargada no cuentan — aproximacion live, igual que las cards).
function missionTaskCounts(m, taskStatusById) {
  let done = 0, total = 0;
  for (const id of parseTaskIds(m.taskIds ?? m.task_ids)) {
    const st = taskStatusById.get(String(id));
    if (!st || st === 'cancelled') continue;
    total++;
    if (st === 'completed') done++;
  }
  return { done, total };
}

// % de avance de una mision: completed=100; si no, tareas done/total (sin tareas conocidas: 0).
function missionPct(m, taskStatusById) {
  if (missionStatus(m) === 'completed') return 100;
  const { done, total } = missionTaskCounts(m, taskStatusById);
  return total > 0 ? Math.round((done / total) * 100) : 0;
}

// (a) Progreso por mision abierta (active+in_progress), ordenado por % desc.
// v2.1 (#2742): conserva status (chip distingue en-curso vs abiertas) y hasAgent
// (criterio NXS en juego = misiones con [AGENTE: en description, query de Ares).
function buildMissionProgress(missions, taskStatusById) {
  return missions
    .filter(m => ACTIVE_STATUSES.has(missionStatus(m)))
    .map(m => {
      const { done, total } = missionTaskCounts(m, taskStatusById);
      return {
        code: String(m.code || '').toUpperCase(), title: m.title || '',
        status: missionStatus(m),
        hasAgent: /\[AGENTE:/i.test(String(m.description || '')),
        reward: missionReward(m), done, total,
        pct: total > 0 ? Math.round((done / total) * 100) : 0
      };
    })
    .sort((a, b) => b.pct - a.pct || b.reward - a.reward);
}

// ===== v2.1 (#2742): paginacion completa =====
// Los endpoints Missions/Tasks CAPAN pageSize a 50 en el backend — pedir una sola
// pagina "grande" daba conteos falsos (29 misiones de 69 abiertas reales; tareas 0/N
// porque el indice solo tenia 50 de 1087). Pagina 1 -> totalPages -> resto en paralelo.
async function fetchAllPages(fetchPage, maxPages = 40) {
  const first = await fetchPage(1).catch(() => null);
  if (!first || !first.ok) return { items: [], partial: true };
  const payload = first.data?.data ?? first.data ?? {};
  const items = [...pickItems(first)];
  const realPages = Number(payload.totalPages) || 1;
  const totalPages = Math.min(realPages, maxPages);
  if (totalPages > 1) {
    const rest = await Promise.all(
      Array.from({ length: totalPages - 1 }, (_, i) => fetchPage(i + 2).catch(() => null))
    );
    for (const r of rest) if (r && r.ok) items.push(...pickItems(r));
  }
  return { items, partial: realPages > maxPages };
}

// Cache TTL del dataset pesado (misiones+tareas completas): el poll de estado live corre
// cada 30s, pero re-traer ~27 paginas cada poll seria abuso. El boton Refrescar fuerza.
const KPI_DATA_TTL_MS = 10 * 60 * 1000;
const _kpiData = { ts: 0, missions: [], tasks: [], partial: false };

/** El hito que declara una mision, si la API lo manda. Tolera los dos casings. */
function missionPhase(m) {
  const v = m?.phaseCode ?? m?.phase_code ?? m?.PhaseCode ?? null;
  const t = v === null || v === undefined ? '' : String(v).trim();
  return t || null;
}

/**
 * CUANTO TRABAJO DEJA FUERA LA LISTA A MANO. Se calcula sobre las misiones que
 * de verdad estan cargadas, no sobre una cifra escrita: si manana entran diez
 * misiones nuevas, el aviso crece solo.
 */
function loQueQuedaFuera(missionByCode) {
  const enLista = new Set();
  for (const codes of Object.values(HITO_MISSIONS)) for (const c of codes) enLista.add(c);
  let fuera = 0, nxsFuera = 0, activas = 0, nxsTotal = 0;
  for (const [code, m] of missionByCode) {
    const st = missionStatus(m);
    if (st !== 'active' && st !== 'in_progress') continue;
    const w = missionReward(m);
    activas++; nxsTotal += w;
    if (!enLista.has(code)) { fuera++; nxsFuera += w; }
  }
  return {
    activas, fuera, nxsTotal, nxsFuera,
    pctNxsFuera: nxsTotal > 0 ? Math.round((nxsFuera / nxsTotal) * 100) : 0
  };
}

// (b) Progreso por hito ponderado por NXS.
//
// DOS CAMINOS, y el bueno se elige solo: si las misiones traen phaseCode, el
// hito sale de la BASE y ninguna mision se queda fuera (las que no tengan hito
// van a "sin hito"). Si no lo traen, se cae a la lista a mano y se devuelve
// `fueraDeLaLista` para que la pantalla DIGA cuanto trabajo no esta contando.
function buildHitoProgress(missionByCode, taskStatusById) {
  const conFase = [...missionByCode.values()].some((m) => missionPhase(m) !== null);
  if (conFase) return buildHitoDesdeLaBase(missionByCode, taskStatusById);
  return { ...buildHitoDesdeLaLista(missionByCode, taskStatusById), fuente: 'lista', fueraDeLaLista: loQueQuedaFuera(missionByCode) };
}

/**
 * El camino bueno: el hito lo declara cada mision. Las que no lo declaran NO
 * desaparecen -- se agrupan en "sin hito", que es justo el dato que hoy no se
 * ve. Se activa solo cuando la API empiece a mandar phaseCode.
 */
function buildHitoDesdeLaBase(missionByCode, taskStatusById) {
  const porHito = new Map();
  let gSum = 0, gPct = 0;
  for (const m of missionByCode.values()) {
    const st = missionStatus(m);
    if (st === 'cancelled') continue;
    const hito = missionPhase(m) ?? '(sin hito)';
    const w = missionReward(m) || 1;
    const pct = missionPct(m, taskStatusById);
    const acc = porHito.get(hito) ?? { wSum: 0, wPct: 0, nDone: 0, nFound: 0 };
    acc.wSum += w; acc.wPct += w * pct; acc.nFound++;
    if (pct >= 100) acc.nDone++;
    porHito.set(hito, acc);
    gSum += w; gPct += w * pct;
  }
  const hitos = [...porHito.entries()]
    // "(sin hito)" siempre al final: es un aviso, no un hito.
    .sort((a, b) => (a[0] === '(sin hito)' ? 1 : b[0] === '(sin hito)' ? -1 : a[0].localeCompare(b[0])))
    .map(([hito, a]) => ({
      hito, pct: a.wSum > 0 ? Math.round(a.wPct / a.wSum) : 0,
      nDone: a.nDone, nFound: a.nFound, nTotal: a.nFound, missing: []
    }));
  return { hitos, globalPct: gSum > 0 ? Math.round(gPct / gSum) : 0, fuente: 'base', fueraDeLaLista: null };
}

function buildHitoDesdeLaLista(missionByCode, taskStatusById) {
  const hitos = [];
  for (const [hito, codes] of Object.entries(HITO_MISSIONS)) {
    let wSum = 0, wPct = 0, nDone = 0;
    const found = [], missing = [];
    for (const code of codes) {
      const m = missionByCode.get(code);
      if (!m) { missing.push(code); continue; }
      const w = missionReward(m) || 1; // reward 0 pesa 1 para no anular la mision
      const pct = missionPct(m, taskStatusById);
      wSum += w; wPct += w * pct;
      if (pct >= 100) nDone++;
      found.push({ code, pct });
    }
    if (!found.length && !missing.length) continue;
    hitos.push({
      hito, pct: wSum > 0 ? Math.round(wPct / wSum) : 0,
      nDone, nFound: found.length, nTotal: codes.length, missing
    });
  }
  // Global ponderado por NXS de todo lo mapeado y encontrado.
  let gSum = 0, gPct = 0;
  for (const [, codes] of Object.entries(HITO_MISSIONS)) {
    for (const code of codes) {
      const m = missionByCode.get(code);
      if (!m) continue;
      const w = missionReward(m) || 1;
      gSum += w; gPct += w * missionPct(m, taskStatusById);
    }
  }
  return { hitos, globalPct: gSum > 0 ? Math.round(gPct / gSum) : 0 };
}

// (c) Velocidad: DONEs por dia (7 dias, hora Colombia) + por agente + PRs entregados
// (pr_url unicos en notas de esos DONEs). Reporta la ventana real de la bitacora cargada.
function dayKeyBogota(ts) {
  return formatearFecha(new Date(ts), { timeZone: 'America/Bogota', year: 'numeric', month: '2-digit', day: '2-digit' });
}
/**
 * EL PR, LEIDO EN LAS TRES FORMAS EN QUE LA CASA LO ESCRIBE.
 *
 * Josse vio "Velocidad: 10 DONEs, 0 PRs" el dia en que hubo doce. No era un
 * cero: era que esto leia UNA sola llave.
 *
 * Medido sobre los DONE de siete dias: la llave `pr` aparece 64 veces y
 * `pr_url` 22. O sea que el tablero no veia el 74% de lo que se declara. Y
 * dentro de esas llaves conviven tres formas: un numero (`pr: 92`), un texto
 * (una URL de GitHub, o "repo N, head") y un objeto (`{numero: N}`).
 *
 * SE DEVUELVE repo#numero Y NO SOLO EL NUMERO: el 84 del Arcade y el 84 del
 * panel no son el mismo PR, y contarlos como uno es la clase de error que hace
 * que el numero baje sin que nadie haya cerrado nada. Cuando el repo no se
 * puede saber, se dice con "?" en vez de fingirlo.
 */
function prDelEvento(e) {
  let n = e?.notas;
  if (typeof n === 'string') { try { n = JSON.parse(n); } catch { return null; } }
  if (!n) return null;
  const crudo = n.pr ?? n.pr_url ?? n.prUrl ?? null;
  if (crudo === null || crudo === undefined) return null;

  let numero = null;
  let repo = String(n.repo || '').trim() || null;

  if (typeof crudo === 'number') {
    numero = crudo;
  } else if (typeof crudo === 'string') {
    const url = crudo.match(/github\.com\/[^/]+\/([^/]+)\/pull\/(\d+)/);
    if (url) { repo = repo || url[1]; numero = Number(url[2]); }
    else {
      // "repo 84, head abc" o "84" a secas.
      const conRepo = crudo.match(/([a-z0-9_.-]+)\s+#?(\d+)/i);
      const solo = crudo.match(/\b(\d{1,5})\b/);
      if (conRepo) { repo = repo || conRepo[1]; numero = Number(conRepo[2]); }
      else if (solo) numero = Number(solo[1]);
    }
  } else if (typeof crudo === 'object') {
    const v = crudo.numero ?? crudo.number ?? crudo.id ?? null;
    if (v !== null && v !== undefined) numero = Number(v);
    repo = repo || String(crudo.repo || '').trim() || null;
  }

  if (!Number.isFinite(numero) || numero <= 0) return null;
  return (repo || '?') + '#' + numero;
}
function buildVelocity(events, now = Date.now()) {
  const days = [];
  for (let i = 6; i >= 0; i--) {
    const ts = now - i * DAY_MS;
    days.push({
      key: dayKeyBogota(ts),
      label: formatearFecha(new Date(ts), { timeZone: 'America/Bogota', weekday: 'short', day: 'numeric' }),
      count: 0
    });
  }
  const byDay = new Map(days.map(d => [d.key, d]));
  const byAgent = new Map();
  const prUrls = new Set();
  let oldestTs = now;
  for (const e of events) {
    const ts = eventTsMs(e);
    if (ts && ts < oldestTs) oldestTs = ts;
    if (!/^\[DONE\]/i.test(e.titulo || '')) continue;
    if (now - ts > 7 * DAY_MS) continue;
    const code = normalizeAgentCode(e.origen);
    if (!code || isLegacyAgent(code)) continue;
    const day = byDay.get(dayKeyBogota(ts));
    if (day) day.count++;
    byAgent.set(code, (byAgent.get(code) || 0) + 1);
    const pr = prDelEvento(e);
    if (pr) prUrls.add(pr);
  }
  const windowDays = Math.min(7, Math.max(1, Math.ceil((now - oldestTs) / DAY_MS)));
  return {
    days,
    total: days.reduce((s, d) => s + d.count, 0),
    byAgent: [...byAgent.entries()].sort((a, b) => b[1] - a[1]),
    prCount: prUrls.size,
    windowDays,
    partialWindow: windowDays < 7 // bitacora cargada no cubre 7 dias completos
  };
}

// (d) NXS equipo vs en juego. v2.1 (#2742), criterios BD verificables:
// - netBalance = SUM(balances) del endpoint /nxs/balances = SUM(amount) de
//   nxs_transactions (creditos - debitos). Se etiqueta NETO — la validacion de Ares
//   comparaba contra SUM(creditos) y por eso "faltaban" exactamente los 9,300 de debitos.
// - atStake = SUM(reward_nexos) de misiones active+in_progress CON [AGENTE: en la
//   description (query de la validacion #2742).
function buildNxsSummary(balancesRes, missionProgress) {
  let netBalance = null; // null = endpoint fallo (se muestra "—", no un 0 falso)
  if (balancesRes && balancesRes.ok) {
    const rows = pickItems(balancesRes);
    netBalance = rows.reduce((s, r) => s + (Number(r.balance ?? r.total ?? r.amount ?? 0) || 0), 0);
  }
  const atStake = missionProgress
    .filter(m => m.hasAgent)
    .reduce((s, m) => s + (m.reward || 0), 0);
  return { netBalance, atStake };
}

// Origenes que escriben en la bitacora y NO son agentes del equipo. Se filtran
// del chip "trabajando" (hallazgo de Argy-Q, #10259: contaba 5 donde hay 4).
// LA LISTA SE MUDO a AgentRegistry.js, que es la fuente unica de identidad:
// aqui era una lista de robots viviendo lejos del registro de agentes, y el
// Gremio necesitaba la misma. Dos copias de la misma lista es como empieza el
// drift que ese modulo existe para matar. El por-que de usar lista negra y no
// blanca -- y la correccion del ejemplo con el que se justifico -- quedo
// escrito alla.

// La medianoche de HOY en Bogota, en ISO Z, para pedirle el dia a la API.
// Bogota es UTC-5 todo el ano (no hay horario de verano), asi que la medianoche
// local son las 05:00Z del mismo dia.
function medianocheBogotaISO() {
  const ahora = new Date();
  const bogota = new Date(ahora.getTime() - 5 * 3600000);
  const y = bogota.getUTCFullYear();
  const m = String(bogota.getUTCMonth() + 1).padStart(2, '0');
  const d = String(bogota.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}T05:00:00Z`;
}

// LA DEUDA DE DISCIPLINA (EQUIPO-T6). Ares: "mostra cuantos eventos llegaron
// sin ninguna de las tres llaves de tarea; ese numero es deuda nuestra y quiero
// verlo bajar".
//
// Se cuenta SOLO sobre los eventos que declaran trabajo ([WIP], [DONE],
// [DONE-UPDATE], [BLOCKED]). Sobre el total daria 70 por ciento (240 de 341
// medido el 4-sep), pero ese numero mezcla COORD, ASSIGN y DICTAMEN, que muchas
// veces no hablan de una tarea concreta: seria inflarlo. Sobre los de trabajo,
// que SI deberian decir cual, eran 66 de 120.
function buildDeudaDeLlaves(events) {
  let total = 0;
  let sinLlave = 0;
  for (const e of (events || [])) {
    if (!isTaskEvent(e.titulo)) continue;
    total++;
    if (!taskIdOfEvent(e)) sinLlave++;
  }
  return { total, sinLlave, pct: total ? Math.round((100 * sinLlave) / total) : null };
}

// (e) Gates/cuellos: misiones activas con TODAS sus tareas done (esperan validacion
// de Josse) + bloqueos abiertos de los agentes (blockedOpen del overlay agregado).
// ===== EQUIPO-T5: la tira de arriba dice EN QUE VA EL TRABAJO =====
//
// Josse (2-sep): "Roadmap 38%", "NXS 625.884/187.300" y "Salud 46 alertas" no
// ayudan a saber en que va el trabajo. No se borra nada: Roadmap y NXS siguen
// enteros en el detalle de abajo; lo que cambia es QUE se pone delante.
//
// POR QUE VENTANA Y NO "HOY" (medido el 4-sep a las 00:03 hora Colombia): en
// ese momento "hoy" tenia 0 entregas y 0 dictamenes -- el dia acababa de
// empezar -- mientras en las ultimas 24 h habia 397 anotaciones, 67 entregas y
// 29 dictamenes. Un chip que marca 0 toda la madrugada se lee como roto, que es
// justo el problema que esta tarea viene a quitar. Asi que se cuenta sobre lo
// que la vista TIENE, y el pie dice cuanto abarca eso: la bitacora publica
// entrega 100 anotaciones como maximo y esas 100 cubrian 4,9 horas.
//
// LO QUE NO ESTA Y POR QUE: Josse pidio "PRs esperando". No se puede saber
// desde aqui: la bitacora decia 3 PRs entregados sin dictamen y la verdad de
// campo eran 0 PRs abiertos en los tres repos; ademas la clave `pr` viene en
// dos formatos (los DONE ponen la URL y los DICTAMEN el numero suelto), asi que
// ese cruce compara cosas distintas. Un indicador que no es confiable no se
// muestra (regla #10098). En su lugar va "trabajando", que si es medible.
function buildPulso(events, missionByCode, gates) {
  const lista = (events || []).filter((e) => eventTsMs(e) > 0);
  const total = lista.length;
  let masViejo = Infinity;
  let masNuevo = 0;
  const porFamilia = new Map();
  const agentes = new Set();
  let entregas = 0;
  let dictamenes = 0;

  for (const e of lista) {
    const ts = eventTsMs(e);
    if (ts < masViejo) masViejo = ts;
    if (ts > masNuevo) masNuevo = ts;
    const t = String(e.titulo || '').toUpperCase();
    if (t.startsWith('[DONE')) entregas++;
    if (t.startsWith('[DICTAMEN')) dictamenes++;
    const code = normalizeAgentCode(e.origen);
    if (code && !ORIGENES_NO_AGENTE.includes(code)) agentes.add(code);
    const mc = missionCodeOfEvent(e);
    if (mc) porFamilia.set(mc, (porFamilia.get(mc) || 0) + 1);
  }

  let familia = null;
  for (const [code, n] of porFamilia) {
    if (!familia || n > familia.n) familia = { code, n, nombre: missionByCode.get(code)?.title || code };
  }

  const horas = (total && masNuevo > masViejo) ? (masNuevo - masViejo) / 3600000 : 0;
  return {
    total,
    horas,
    familia,
    entregas,
    dictamenes,
    agentes: agentes.size,
    gates: gates ? gates.count : null,
    deuda: buildDeudaDeLlaves(lista),
    gatesDetalle: gates ? `${gates.readyForValidation.length} mision(es) esperando validacion + ${gates.blockedOpen.length} bloqueo(s) abierto(s)` : ''
  };
}

function buildGates(missionProgress, agents) {
  const readyForValidation = missionProgress.filter(m => m.total > 0 && m.done === m.total);
  const blockedOpen = (agents || [])
    .filter(a => a.blockedOpen)
    .map(a => ({ agentCode: a.agentCode, missionCode: a.missionCode || null }));
  return { readyForValidation, blockedOpen, count: readyForValidation.length + blockedOpen.length };
}

// (f) Salud: misiones activas estancadas >30d, tareas huerfanas (pending/in_progress
// en misiones completed), WIPs de bitacora sin cierre >24h.
// v2 fix (hallazgo Argy-Q #2699): nexo_missions.updated_at NO se actualiza al postear
// DONE/WIP sobre tareas — sola, marca estancadas falsas (ej. AUDIT-MANUAL: 47d en BD,
// actividad real 0d). Se cruza con la ultima mencion del code en la bitacora cargada:
// si la mision aparece en la ventana reciente, NO esta estancada aunque BD diga 30d+.
function buildHealth(missions, taskStatusById, events, now = Date.now()) {
  const lastMentionByCode = new Map();
  for (const e of events) {
    const mc = missionCodeOfEvent(e);
    if (!mc) continue;
    const ts = eventTsMs(e);
    if (ts > (lastMentionByCode.get(mc) || 0)) lastMentionByCode.set(mc, ts);
  }
  const stalled = missions.filter(m => {
    if (!ACTIVE_STATUSES.has(missionStatus(m))) return false;
    const ts = new Date(m.updatedAt ?? m.updated_at ?? m.createdAt ?? m.created_at ?? 0).getTime();
    if (!(ts > 0 && (now - ts) > 30 * DAY_MS)) return false;
    const mention = lastMentionByCode.get(String(m.code || '').toUpperCase());
    return !mention; // con actividad real en la ventana de bitacora -> viva
  }).map(m => ({ code: String(m.code || '').toUpperCase(), title: m.title || '' }));

  let orphanCount = 0;
  const orphanSample = [];
  for (const m of missions) {
    if (missionStatus(m) !== 'completed') continue;
    for (const id of parseTaskIds(m.taskIds ?? m.task_ids)) {
      const st = taskStatusById.get(String(id));
      if (st === 'pending' || st === 'in_progress') {
        orphanCount++;
        if (orphanSample.length < 4) orphanSample.push(String(m.code || '').toUpperCase());
      }
    }
  }

  // WIP sin DONE/BLOCKED/WIP-PAUSED posterior de la misma mision+agente, >24h.
  const resolvedKeys = new Set(), seenWip = new Set(), wipStale = [];
  for (const e of events) { // events desc: lo mas reciente por clave gana
    const code = normalizeAgentCode(e.origen);
    if (!code || isLegacyAgent(code)) continue;
    const mc = missionCodeOfEvent(e);
    if (!mc) continue;
    const key = code + '|' + mc;
    const t = e.titulo || '';
    if (/^\[(DONE|BLOCKED|WIP-PAUSED|REVIEW-OK)\]/i.test(t)) { resolvedKeys.add(key); continue; }
    if (/^\[WIP\]/i.test(t)) {
      if (resolvedKeys.has(key) || seenWip.has(key)) continue;
      seenWip.add(key);
      const age = now - eventTsMs(e);
      if (age > DAY_MS) wipStale.push({ agentCode: code, missionCode: mc, ageMs: age });
    }
  }
  return { stalled, orphanCount, orphanSample, wipStale, alertCount: stalled.length + (orphanCount > 0 ? 1 : 0) + wipStale.length };
}

// Barra generica de porcentaje.
/**
 * CINCO FILAS Y UN "VER MAS" (PANEL-EQUIPO-ORDEN-T1).
 *
 * Josse lo pidio mirando la pantalla: los widgets largos mandaban lo demas muy
 * abajo. No es un problema de gusto -- lo que queda debajo del pliegue no se
 * mira, y lo que no se mira no existe.
 *
 * SE MUESTRA EL TOTAL SIEMPRE, tambien cuando esta plegado. Un widget que dice
 * "5" cuando hay treinta miente por omision, que es la clase de numero que esta
 * casa lleva media semana quitando de encima.
 *
 * @param {Array} filas    lo que hay
 * @param {Function} pintar como se pinta una fila
 * @param {string} clave   para acordarse de si el usuario lo dejo abierto
 * @param {object} abiertos Set con las claves desplegadas
 */
const TOPE_PLEGADO = 5;
function plegarFilas(filas, pintar, clave, abiertos) {
  const total = filas.length;
  if (total <= TOPE_PLEGADO) return filas.map(pintar).join('');
  const abierto = abiertos && abiertos.has(clave);
  const visibles = abierto ? filas : filas.slice(0, TOPE_PLEGADO);
  const restantes = total - TOPE_PLEGADO;
  return visibles.map(pintar).join('')
    + `<button data-aad-vermas="${clave}" type="button"
         style="width:100%;margin-top:8px;text-align:left;background:#0d1117;border:1px solid #21262d;
                border-radius:6px;padding:7px 10px;color:#8b949e;font-size:0.875rem;cursor:pointer">
         ${abierto ? 'Ver menos' : 'Ver las ' + restantes + ' restantes'}
         <span style="float:right;color:#6e7681">${total} en total</span>
       </button>`;
}

function pctBar(pct, color) {
  const c = color || (pct >= 100 ? '#3fb950' : pct >= 50 ? '#58a6ff' : '#d29922');
  return `
    <div style="flex:1;height:6px;background:#0d1117;border-radius:4px;overflow:hidden">
      <div style="width:${Math.min(100, Math.max(0, pct))}%;height:100%;background:${c};transition:width .3s"></div>
    </div>`;
}
// ===================== fin bloque KPIs =====================

// #2465 — traduce el titulo de bitacora ([TAG] texto - MISSION) a lenguaje natural.
const EVENT_VERB = {
  wip: 'Trabajando en', done: 'Termino', blocked: 'Bloqueado en', redirect: 'Cambio de rumbo en',
  assign: 'Asignado', 'review-ok': 'Aprobado', 'review-fail': 'Correcciones en',
  plan: 'Planificando', note: 'Nota', heartbeat: 'Latido', unblock: 'Desbloqueado', approve: 'Aprobado'
};
function naturalizeEvent(titulo) {
  if (!titulo) return '';
  const m = String(titulo).match(/^\[([A-Z-]+)\]\s*(.*)$/i);
  if (!m) return titulo;
  const tag = m[1].toLowerCase();
  let rest = m[2].replace(/\s*[-—]\s*[A-Z0-9][A-Z0-9-]*V?\d*(-\d+)?\s*$/i, '').trim(); // quita el sufijo MISSION-CODE
  const verb = EVENT_VERB[tag] || tag.toUpperCase();
  return rest ? `${verb}: ${rest}` : verb;
}

// Barra de progreso de tareas (done/total).
function tasksBar(done, total) {
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  const color = pct >= 100 ? '#3fb950' : pct >= 50 ? '#58a6ff' : '#d29922';
  return `
    <div style="display:flex;align-items:center;gap:8px;margin-top:8px">
      <div style="flex:1;height:6px;background:#0d1117;border-radius:4px;overflow:hidden">
        <div style="width:${pct}%;height:100%;background:${color};transition:width .3s"></div>
      </div>
      <span style="font-size:0.875rem;color:#8b949e;white-space:nowrap">${done}/${total} tareas</span>
    </div>`;
}

// Extrae el array de items de una respuesta ApiBase (varias formas).
function pickItems(res) {
  if (!res || !res.ok) return [];
  const d = res.data?.data ?? res.data ?? [];
  return Array.isArray(d) ? d : (d.items || []);
}
function parseTaskIds(v) {
  if (Array.isArray(v)) return v;
  if (typeof v === 'string') { try { const p = JSON.parse(v); return Array.isArray(p) ? p : []; } catch { return []; } }
  return [];
}
function missionCodeOfEvent(e) {
  let n = e?.notas;
  if (typeof n === 'string') { try { n = JSON.parse(n); } catch { return ''; } }
  return String(n?.mission_code || n?.missionCode || '').toUpperCase();
}
function eventTsMs(e) { return new Date(e?.tsUtc || e?.ts_utc || 0).getTime(); }

// ===== EQUIPO-T1: la tarjeta muestra la TAREA, no la mision =====
// El endpoint /agents/dashboard entrega mission_code pero NO task_id ni pasos
// (medido sobre el DTO en origin/main, 3-sep). El task_id SI viaja en el JSON de
// notas de cada [WIP]/[DONE]/[BLOCKED], y las tareas ya vienen cargadas para los
// KPI: con eso la tarjeta resuelve la tarea sin UNA SOLA peticion extra.
const TASK_EVENT_TAGS = ['[WIP]', '[DONE]', '[DONE-UPDATE]', '[BLOCKED]'];
function isTaskEvent(titulo) {
  const s = String(titulo || '').toUpperCase();
  return TASK_EVENT_TAGS.some(tag => s.startsWith(tag));
}
// LAS CUATRO LLAVES (EQUIPO-T6). "Tarea: no se puede saber todavia" no era un
// hueco de datos: era un contrato de nombres roto. Medido en 36 h: Ares manda
// mission_code 158 veces y task_id CERO; Argy-Q manda task_code 38; Panel manda
// task_id 36 y task_code cero; Argy-API manda task_id 21 mas task_uuid 6. La
// vista buscaba UNA sola llave, asi que no encontraba las de tres cuartas
// partes del equipo. Se leen las cuatro, en el orden en que identifican: el
// uuid y el id apuntan a la tarea; el code tambien, si el catalogo lo conoce.
function taskIdOfEvent(e) {
  let n = e?.notas;
  if (typeof n === 'string') { try { n = JSON.parse(n); } catch { return ''; } }
  const bruto = String(n?.task_id || n?.taskId || n?.task_uuid || n?.taskUuid || n?.task_code || n?.taskCode || '').trim();
  // UN task_code QUE ES SOLO UN NUMERO NO ES UNA TAREA: ES UN PR.
  //
  // Es la regla de la casa -- Ares firma sus [VERIFICADO] con el numero del PR
  // en task_code -- y aqui se leia como llave de tarea. Resultado: se buscaba
  // "82" en el catalogo, no aparecia, y su tarjeta decia "declaro una tarea que
  // no esta en el catalogo". La tarjeta acusaba a Ares de un error suyo.
  if (/^\d{1,5}$/.test(bruto)) return '';
  return bruto;
}
// Devuelve {id,title,status,tsMs} de la tarea que el agente declaro mas
// recientemente, o {reason} diciendo POR QUE no se puede saber. REGLA: nunca se
// inventa. Un agente que no declara task_id no tiene una tarea "desconocida":
// tiene un evento que no la declaro, y la tarjeta lo dice con esas palabras.
/** El ultimo evento del agente ANTERIOR a `hasta` que si declaro una tarea. */
function heredarTareaDelUltimoDone(agentCode, events, hasta) {
  let best = null;
  for (const e of (events || [])) {
    if (normalizeAgentCode(e.origen) !== agentCode) continue;
    if (!isTaskEvent(e.titulo)) continue;
    const ts = eventTsMs(e);
    if (!ts || ts > hasta) continue;
    const tid = taskIdOfEvent(e);
    if (!tid) continue;
    if (best && ts <= best.ts) continue;
    best = { ts, tid };
  }
  return best;
}

function resolveCurrentTask(agentCode, events, taskById, missionByCode) {
  let best = null;
  for (const e of (events || [])) {
    if (normalizeAgentCode(e.origen) !== agentCode) continue;
    if (!isTaskEvent(e.titulo)) continue;
    const ts = eventTsMs(e);
    if (best && ts <= best.ts) continue;
    best = { ts, tid: taskIdOfEvent(e), mc: missionCodeOfEvent(e) };
  }
  if (!best) return { reason: 'sin eventos de trabajo recientes' };

  // ANTES DE RENDIRSE, LA FAMILIA (EQUIPO-T7). Si el evento no trajo llave de
  // tarea pero si mission_code, se dice en que anda aunque no se sepa cual es
  // la tarea exacta. Medido: Ares manda mission_code en 158 de 166 eventos y
  // task_id en cero, asi que sin este escalon su tarjeta nunca diria nada.
  const familia = (codigo) => {
    if (!codigo) return null;
    const m = missionByCode && missionByCode.get ? missionByCode.get(codigo) : null;
    return String((m && m.title) || codigo).trim() || null;
  };

  if (!best.tid) {
    // ANTES DE RENDIRSE, EL ULTIMO DONE DEL MISMO AGENTE (PANEL-EQUIPO-ORDEN-T1).
    //
    // Argy-D manda task_code en su [DONE] y NO lo repite en el [DONE-UPDATE]
    // que viene detras, que es lo razonable: es el mismo trabajo. Con solo
    // mirar el ultimo evento, su tarjeta se quedaba muda justo despues de
    // entregar.
    //
    // No se inventa nada: se busca hacia atras un evento SUYO que si declaro
    // tarea, y se dice que viene de ahi. Si no hay ninguno, sigue diciendo que
    // no se puede saber.
    const heredado = heredarTareaDelUltimoDone(agentCode, events, best.ts);
    if (heredado) {
      const t2 = taskById.get(String(heredado.tid));
      if (t2) {
        return {
          id: String(heredado.tid),
          title: String(t2.title || '').trim() || String(heredado.tid),
          status: String(t2.status || '').toLowerCase(),
          tsMs: best.ts,
          heredada: true
        };
      }
    }
    const f = familia(best.mc);
    return f
      ? { familia: f, tsMs: best.ts, reason: 'su ultimo evento no declaro la tarea' }
      : { reason: 'su ultimo evento no declaro la tarea' };
  }
  const t = taskById.get(String(best.tid));
  if (!t) {
    const f = familia(best.mc);
    return f
      ? { familia: f, tsMs: best.ts, reason: 'declaro una tarea que no esta en el catalogo' }
      : { reason: 'declaro una tarea que no esta en el catalogo' };
  }
  return {
    id: String(best.tid),
    title: String(t.title || '').trim() || '(tarea sin titulo)',
    status: String(t.status || '').toLowerCase(),
    tsMs: best.ts
  };
}

// Enriquece un agente con datos de gamificacion (mision, NXS, tareas done/total, racha, evento natural).
// ===== EQUIPO-T6: SIGUE AHI, O SE APAGO SIN AVISAR =====
//
// Josse: "el panel no nos muestra que P, Q y tu estan en Loop, esto ha sido una
// constante bastante dificil de manejar".
//
// POR QUE NO BASTABA EL BADGE QUE YA HABIA. El "EN LOOP" v3 existe desde #2751
// y hoy esta apagado para TODOS, y no por un bug: sus dos senales dependen de
// convenciones que el equipo dejo de usar. Medido el 4-sep:
//   - Senal 1, [HEARTBEAT] propio en 45 min: Q llevaba 574 minutos sin latir,
//     Panel 591 y API 947... mientras los tres escribian cada pocos minutos. El
//     protocolo pide latido cada 30 min SOLO SI ESTAS IDLE, asi que su ausencia
//     significa "esta ocupado", no "esta muerto". La premisa del comentario de
//     la v3 -- "sin evento en 45, el agente NO esta cumpliendo loop" -- era
//     justo al reves.
//   - Senal 2, ser target de un [ASSIGN] con LOOP en el titulo: 0 de 37 ASSIGN
//     de la ultima semana lo tienen. Ares no los escribe asi.
//
// LA SENAL DE VIDA ES CUALQUIER EVENTO. Un agente que entrega cada cinco
// minutos esta mas vivo que uno que solo late. El latido cuenta como uno mas,
// no como el unico. Con este criterio, en el momento de medir: Ares 2 min, Q 6,
// Panel 10 y API 26 -> vivos; Argy-W 2304 y Desktop 2306 -> apagados. Eso si
// coincide con lo que estaba pasando.
//
// Y EL TRAMO QUE FALTABA ES EL DE EN MEDIO. Un Argy que se cae no anuncia nada:
// simplemente deja de escribir, como le paso a Argy-A el 3-sep a las 19:40 sin
// que a nadie le sonara la alarma. CALLADO es ese hueco -- ya no responde, pero
// todavia no es seguro que se haya caido -- y es el estado que hace falta ver.
const VIVO_MS = 35 * 60 * 1000;
const CALLADO_MS = 2 * 60 * 60 * 1000;
function vidaDe(lastTs, now = Date.now()) {
  if (!lastTs) {
    return { clave: 'nunca', etiqueta: 'sin rastro en lo cargado', color: '#6e7681',
      detalle: 'No aparece en las anotaciones que la vista cargo. No significa que no exista ni que nunca haya trabajado: su ultima anotacion quedo fuera de esta ventana. Con la API 1.6.45 la ventana pasa a ser el dia entero y esto se vuelve mas preciso.' };
  }
  const min = Math.round((now - lastTs) / 60000);
  const texto = min < 60 ? `hace ${min} min` : `hace ${(min / 60).toFixed(1)} h`;
  if (now - lastTs <= VIVO_MS) {
    return { clave: 'vivo', etiqueta: `respondiendo (${texto})`, color: '#3fb950',
      detalle: 'Escribio en los ultimos 35 minutos. La senal es CUALQUIER anotacion suya, no solo el latido: quien entrega seguido no late, y eso no lo hace menos vivo.' };
  }
  if (now - lastTs <= CALLADO_MS) {
    return { clave: 'callado', etiqueta: `callado ${texto}`, color: '#d29922',
      detalle: 'Entre 35 minutos y 2 horas sin escribir nada. Puede estar en algo largo o haberse caido sin avisar: es el hueco donde se pierde un Argy y nadie se entera.' };
  }
  return { clave: 'apagado', etiqueta: `apagado ${texto}`, color: '#6e7681',
    detalle: 'Mas de 2 horas sin una sola anotacion. Si deberia estar en loop, no lo esta.' };
}

// ===== ARGYS-VIVO-FIX-T1: EL TABLERO DICE LA MISMA VERDAD QUE EL GREMIO =====
//
// Josse, 7-sep: abrio las dos vistas a la vez y no coincidian. El Gremio del
// Arcade mostraba a Argy-A y a Argy-P con trabajo en curso -- lo que de verdad
// estaba pasando -- y este tablero los daba por DONE / sin tarea.
//
// LAS DOS TENIAN RAZON SOBRE DATOS DISTINTOS. El Gremio mira LA ULTIMA ETIQUETA
// que el Argy escribio en la bitacora. El tablero miraba a.status, una marca
// aparte que calcula el endpoint. Dos fuentes para la misma pregunta terminan
// siempre en dos respuestas.
//
// Y EL ERROR DE FONDO ERA CONCEPTUAL, no de datos: isSinTrabajo() contaba como
// "trabajando" solo a wip, active, assigned, blocked y redirect. Un DONE no
// estaba en esa lista, asi que a quien ACABA DE ENTREGAR lo reportaba como que
// no tiene nada que hacer. Entregar no es estar ocioso: es el momento de mas
// trabajo del dia.
//
// Aqui se calcula igual que el Gremio: ultima etiqueta + antiguedad, y el
// cierre de sesion manda. La tabla es la MISMA que agentes.js del Arcade, a
// proposito: si un dia se separan, se separan las dos verdades otra vez.
const FAMILIAS = [
  // Esta metido en algo ahora mismo.
  //
  // [STOP ESTA AQUI Y NO EN DORMIDO (ARC-ESTADOS-CIERRE-T1). Josse fotografio
  // el tablero el 10-sep a las 17:50: Ares salia "cerro la sesion" porque el
  // [STOP] que se estaba mirando lo habia FIRMADO EL, dirigido a Argy-D.
  //
  // cerroYNoVolvio() mira los eventos PROPIOS de cada agente, asi que un
  // [STOP] solo puede caer en la fila de quien lo escribe -- nunca en la de su
  // destinatario. Tenerlo en "dormido" cerraba exactamente a la persona
  // equivocada: la que sigue despierta repartiendo el cierre de los demas.
  //
  // La regla de Ares, literal: el cierre propio apaga al instante; el STOP
  // cuenta para su destinatario, nunca para quien lo firma.
  // ARC-GREMIO-ESTADOS-T1 (Josse 23-sep, Ares #17995), la misma tabla del Arcade (estadoAgente.js): armar el loop,
  // un run rojo, una prioridad o una decision son trabajo en curso; servir, medir vivo y mergear son entregas.
  // Quedo solo en el Arcade y check:estados la marco roja el 25-sep: el tablero pintaba 'esperando' a quien servia.
  ['trabajando', ['[WIP', '[COORD', '[ASSIGN', '[FINDING', '[CORRECCION', '[REDIRECT', '[STOP', '[LOOP-ARMADO', '[RUN-ROJO', '[PRIORIDAD', '[DECISION']],
  // Acaba de soltar algo. El dictamen de Q es su entrega, igual que un DONE es
  // la de quien construye.
  ['entregando', ['[DONE', '[DICTAMEN', '[RETIRAR-DICTAMEN', '[REVIEW-OK', '[REVIEW-CHANGES', '[SERVIDO', '[VIVO', '[MERGED']],
  ['trabado', ['[BLOCKED', '[GATE']],
  ['esperando', ['[HEARTBEAT', '[ACK']],
  // DORMIDO: SOLO el cierre que el agente firma sobre SI MISMO.
  // [LOOP-DETENIDO entra aqui porque es exactamente eso -- "apague mi loop" --
  // y hasta hoy no estaba en ninguna lista, asi que caia en "esperando" y el
  // tablero pintaba despierto a quien acababa de irse.
  ['dormido', ['[SESSION-CLOSE', '[LOOP-DETENIDO', '[HANDOFF']]
];

function familiaDe(titulo) {
  const t = String(titulo || '').toUpperCase();
  for (const [familia, prefijos] of FAMILIAS) {
    for (const p of prefijos) if (t.startsWith(p)) return familia;
  }
  return 'esperando';
}

// El corte de antiguedad. Es el mismo numero que el Arcade (ARC-GREMIO-T4) y
// por la misma razon: el maximo que la casa permite entre latidos son 30 min,
// asi que un vigilante que corta en 30 no vigila, acusa. 45 deja margen.
const CORTE_MS = 45 * 60 * 1000;

// EL CIERRE GANA SOBRE LA ULTIMA ETIQUETA SUELTA (suma de Josse, 7-sep noche).
//
// Ares cierra y despues sigue dejando notas; con solo mirar la ultima etiqueta,
// el tablero lo pintaria trabajando. Pero tampoco vale que el cierre sea
// eterno: Ares cerro anoche y esta manana volvio.
//
// VOLVER SE DECLARA, NO SE DEDUCE. Mi primer intento contaba como -volvio-
// cualquier marcador de la familia trabajando, y eso incluye [COORD]: o sea que
// el caso EXACTO que reporto Josse -- Ares cierra y deja un COORD despues -- se
// seguia pintando trabajando. Lo cazo la prueba, no la lectura.
//
// Los marcadores con los que un Argy de esta casa ARRANCA son [WIP] y [ACK]:
// asi volvio Ares hoy ([WIP] Ares arranca el 8-sep) y asi vuelvo yo cada
// manana. Una nota suelta no es volver.
const VUELVE = ['[WIP', '[ACK'];
function cerroYNoVolvio(codigo, eventos) {
  const mios = (eventos || []).filter((e) => normalizeAgentCode(e.origen) === codigo);
  // eventTsMs devuelve 0 cuando el evento no trae fecha, asi que se descarta
  // con > 0: un 0 tratado como fecha valida seria 1970 y ganaria siempre.
  let cierre = 0;
  for (const e of mios) {
    const ts = eventTsMs(e);
    if (ts > 0 && familiaDe(e.titulo) === 'dormido' && ts > cierre) cierre = ts;
  }
  if (cierre === 0) return false;
  return !mios.some((e) => {
    const ts = eventTsMs(e);
    if (ts <= cierre) return false;
    const t = String(e.titulo || '').toUpperCase();
    return VUELVE.some((p) => t.startsWith(p));
  });
}

// EN PALABRAS DE JOSSE, no en las nuestras. El tablero decia WIP, DONE, IDLE y
// OFF: son las etiquetas con las que hablamos entre agentes, y el que mira esta
// pantalla es el dueno. La tarea lo pide explicito -- texto permitido, pero
// digerible -- asi que aqui va lo que significan, en pocas palabras.
const COMO_VA = {
  trabajando: { label: 'trabajando', color: '#3fb950', dotShadow: 'rgba(63,185,80,0.5)',
    frase: 'Metido en algo ahora mismo.' },
  entregando: { label: 'entregando', color: '#58a6ff', dotShadow: 'rgba(88,166,255,0.5)',
    frase: 'Acaba de soltar trabajo. No esta libre: esta cerrando.' },
  trabado:    { label: 'trabado', color: '#d29922', dotShadow: 'rgba(210,153,34,0.5)',
    frase: 'Esta esperando algo que no depende de el.' },
  esperando:  { label: 'esperando', color: '#8b949e', dotShadow: 'rgba(139,148,158,0.4)',
    frase: 'Vivo y sin nada entre manos.' },
  dormido:    { label: 'cerro la sesion', color: '#30363d', dotShadow: 'transparent',
    frase: 'Cerro y no ha vuelto. Hasta que arranque de nuevo, no lee nada.' },
  callado:    { label: 'sin responder', color: '#6e7681', dotShadow: 'transparent',
    frase: 'Lleva mas de 45 minutos sin escribir una sola linea. Si deberia estar en loop, no lo esta.' }
};

/**
 * El estado de un Argy, con la MISMA regla que el Gremio del Arcade.
 *
 * El orden importa y es el que pidio Josse: primero si cerro, despues si lleva
 * mucho callado, y solo entonces que dice su ultima etiqueta. Al reves, un
 * agente que cerro hace ocho horas se pintaria por su ultima etiqueta como si
 * siguiera ahi.
 */
function comoVaElArgy(a, eventos) {
  if (cerroYNoVolvio(a.agentCode, eventos)) return { clave: 'dormido', ...COMO_VA.dormido };
  if (a.lastTs && Date.now() - a.lastTs > CORTE_MS) return { clave: 'callado', ...COMO_VA.callado };
  const f = familiaDe(a.lastEvent?.titulo);
  return { clave: f, ...COMO_VA[f] };
}

// Sin trabajo asignado: no esta trabajando/asignado ni tiene mision -> senal para Ares.
/**
 * Si Ares le puede dar trabajo: ni esta metido en algo, ni acaba de entregar,
 * ni esta trabado, y ademas no tiene mision.
 *
 * ANTES MIRABA a.status Y ESE ERA EL DEFECTO QUE VIO JOSSE: la lista de
 * -trabajando- eran wip, active, assigned, blocked y redirect, y un DONE no
 * estaba. A quien acababa de ENTREGAR se le reportaba como que no tiene nada
 * que hacer. Entregar no es estar ocioso.
 *
 * Se pregunta por comoVa y no se deja el camino viejo de respaldo: dos formas
 * de decidir lo mismo son dos verdades, y una de las dos acaba pintando otra
 * cosa. Es la misma razon por la que en el Arcade se borro enfriar() al entrar
 * el interruptor.
 */
const EN_MARCHA = ['trabajando', 'entregando', 'trabado'];
function isSinTrabajo(a) {
  return !EN_MARCHA.includes(a.comoVa?.clave) && !a.missionCode;
}

function enrichAgent(a, missionByCode, taskStatusById, events, taskById) {
  const code = String(a.missionCode || '').toUpperCase();
  const mission = code ? missionByCode.get(code) : null;
  let tasksDone = 0, tasksTotal = 0, reward = null, missionTitle = null;
  if (mission) {
    reward = mission.rewardNexos ?? mission.reward_nexos ?? null;
    missionTitle = mission.title || null;
    for (const id of parseTaskIds(mission.taskIds ?? mission.task_ids)) {
      const st = taskStatusById.get(String(id));
      if (!st || st === 'cancelled') continue;
      tasksTotal++;
      if (st === 'completed') tasksDone++;
    }
  }
  const streak = (events || []).filter(e =>
    normalizeAgentCode(e.origen) === a.agentCode && /^\[DONE\]/i.test(e.titulo || '')
  ).length;
  // El orden importa: comoVa se calcula PRIMERO porque isSinTrabajo lo usa.
  const comoVa = comoVaElArgy(a, events);
  return {
    ...a, missionTitle, reward, tasksDone, tasksTotal, streak, comoVa,
    currentTask: resolveCurrentTask(a.agentCode, events, taskById || new Map(), missionByCode),
    lastNatural: naturalizeEvent(a.lastEvent?.titulo),
    sinTrabajo: isSinTrabajo({ ...a, comoVa }), vida: vidaDe(a.lastTs)
  };
}

// Cola de reviews de Ares: entregas [DONE] de Argys ejecutores SIN un [REVIEW-OK]/merge
// posterior de Ares para la misma mision. 1 por agente+mision (la mas reciente).
// ===== EQUIPO-T4: DIARIO DE HOY =====
// AUDIENCIA (regla #5638): Josse. Es literalmente su pedido del 2-sep: "que todo
// esto no se pierda en el chat, alguna modalidad de notificaciones RESUMIDAS
// para yo verificar". El lector es el dueno, no un tecnico: una linea por cosa,
// sin corchetes, sin codigos de tarea, agrupado por familia con su nombre.
//
// MEDIDO (3-sep): 75 lineas relevantes en el dia -46 entregas, 21 dictamenes,
// 7 hallazgos, 1 bloqueo- en cuatro familias. 67 traen mission_code y OCHO NO.
// Esas ocho NO se esconden ni se meten a la fuerza en una familia: van juntas
// al final, dichas como lo que son. Si desaparecieran, el diario mostraria 67
// de 75 y se veria completo, que es la clase de mentira mas cara de un tablero.
const DIARIO_TIPOS = [
  { marca: '[DONE',     verbo: 'entrego',   color: '#3fb950' },
  { marca: '[DICTAMEN', verbo: 'dictamino', color: '#bc8cff' },
  { marca: '[FINDING',  verbo: 'encontro',  color: '#d29922' },
  { marca: '[BLOCKED',  verbo: 'se trabo',  color: '#ff7b72' }
];
const SIN_FAMILIA = '(sin familia declarada)';
// EQUIPO-T5: dias sin escribir a partir de los cuales un agente se pliega.
// Va en el modulo y no dentro del closure: renderGrid corre antes de que se
// evalue el cuerpo, y un const ahi dentro reventaba con TDZ (lo caso el smoke).
const DIAS_INACTIVO = 7;
// Tope de la consulta publica de bitacora (1.6.47 lo fija en 500).
const TOPE_BITACORA = 500;
// El titulo de la bitacora ya viene en lenguaje humano por regla nuestra. Lo
// unico que se le quita es el marcador de adelante, que al dueno no le dice nada.
function limpiarTitulo(t) {
  const s = String(t || '').trim();
  const cierre = s.indexOf(']');
  return (s.startsWith('[') && cierre > 0) ? s.slice(cierre + 1).trim() : s;
}
function tipoDe(titulo) {
  const t = String(titulo || '').toUpperCase();
  return DIARIO_TIPOS.find((d) => t.startsWith(d.marca)) || null;
}
function buildDiario(events, missionByCode, now = Date.now()) {
  const hoy = dayKeyBogota(now);
  const porFamilia = new Map();
  let lineas = 0;
  for (const e of (events || [])) {
    const ts = eventTsMs(e);
    if (!ts || dayKeyBogota(ts) !== hoy) continue;
    const tipo = tipoDe(e.titulo);
    if (!tipo) continue;
    const code = missionCodeOfEvent(e);
    const clave = code || SIN_FAMILIA;
    if (!porFamilia.has(clave)) {
      const m = code ? missionByCode.get(code) : null;
      porFamilia.set(clave, { nombre: m?.title || clave, esSinFamilia: !code, filas: [] });
    }
    porFamilia.get(clave).filas.push({
      hora: formatearFecha(new Date(ts), { timeZone: 'America/Bogota', hour: '2-digit', minute: '2-digit' }, { parte: 'hora' }),
      quien: (AGENT_PROFILE[normalizeAgentCode(e.origen)] || {}).short || e.origen,
      verbo: tipo.verbo,
      color: tipo.color,
      que: limpiarTitulo(e.titulo),
      ts
    });
    lineas++;
  }
  // Las que no declararon familia van al FINAL, no ocultas: son 8 de 75 y el
  // dueno tiene derecho a saber que existen y que nadie las clasifico.
  const grupos = [...porFamilia.values()].sort((a, b) =>
    (a.esSinFamilia === b.esSinFamilia) ? b.filas.length - a.filas.length : (a.esSinFamilia ? 1 : -1));
  for (const g of grupos) g.filas.sort((a, b) => b.ts - a.ts);
  // Cuantas entradas alcanzo a mirar. El endpoint tapa en 100 (medido 3-sep),
  // asi que el diario NO ve el dia entero y la pantalla tiene que decirlo.
  return { grupos, lineas, leidas: (events || []).length, masViejo: (events || []).length ? eventTsMs(events[events.length - 1]) : null };
}

// ===== EQUIPO-T3: bloque DECISIONES DE JOSSE =====
// AUDIENCIA (regla #5638): Josse en la Sala de Maquinas. Es la vista donde el
// DECIDE, asi que cada fila trae la pregunta en su idioma y que se destraba.
//
// FUENTE: metadata.gate_josse de las tareas, que YA vienen cargadas para los
// KPI. Cero peticiones extra.
//
// POR QUE NO SE FILTRA POR "TAREA ABIERTA", que era lo obvio: medido el 3-sep,
// 9 de los 28 gates reales viven en tareas YA CERRADAS. Su pregunta sigue sin
// respuesta aunque la tarea se entrego -dos de esas son mias-. Filtrar por
// tarea abierta las escondia justo a ellas, que son las que MAS riesgo tienen
// de quedar olvidadas: nadie las va a ver mirando pendientes. Se muestran todas
// y las de tarea cerrada van marcadas.
//
// Y 13 tareas traen la clave gate_josse diciendo literalmente "Ninguno": la
// clave existe y el gate no. Contarlas seria inflar la lista con nada.
const GATE_NO_ES_GATE = 'ninguno';
function gateDeTarea(t) {
  const m = t?.metadata || t?.Metadata;
  if (!m) return null;
  const g = m.gate_josse ?? m.gateJosse;
  if (g == null) return null;
  const texto = String(g).trim();
  if (!texto) return null;
  if (texto.toLowerCase().startsWith(GATE_NO_ES_GATE)) return null;
  return texto;
}
function buildDecisiones(tasks) {
  const filas = [];
  let conClaveVacia = 0;
  for (const t of (tasks || [])) {
    const m = t?.metadata || t?.Metadata;
    if (m && (m.gate_josse != null || m.gateJosse != null)) {
      const g = gateDeTarea(t);
      if (!g) { conClaveVacia++; continue; }
      const estado = String(t.status || '').toLowerCase();
      filas.push({
        pregunta: g,
        tarea: String(t.title || '').trim() || '(tarea sin titulo)',
        entregada: estado === 'completed' || estado === 'cancelled',
        // OJO con el nombre: esto es cuando se TOCO la tarea por ultima vez, no
        // cuando empezo a esperar la decision. Ese dato no existe hoy, asi que
        // la pantalla lo dice con esas palabras y no promete lo que no sabe.
        tocadaMs: new Date(t.updatedAt || t.updated_at || 0).getTime() || null
      });
    }
  }
  // Las de tarea entregada van al final: siguen abiertas, pero no frenan a nadie.
  filas.sort((a, b) => (a.entregada === b.entregada)
    ? (a.tocadaMs || 0) - (b.tocadaMs || 0)
    : (a.entregada ? 1 : -1));
  return { filas, conClaveVacia };
}

// ===== EQUIPO-T2: bloque COMPUERTA =====
// Distinto de buildReviewQueue: aquella son los DONE que ARES no reviso, por
// MISION. Esta es por PR y por dictamen de ARGY-Q: quien esta esperando el visto
// bueno, en orden de llegada, y que se mergeo hoy.
//
// LIMITE MEDIDO Y DECLARADO EN PANTALLA (3-sep): la cobertura del PR en los DONE
// no es del 100%. En 14 dias: Panel 47/53, Argy-API 20/24, Desktop 16/22, W 4/8.
// Por eso el bloque dice cuantas entregas de hoy NO se pudieron amarrar a un PR.
// Una lista corta SIN ese aviso se lee como "no hay nada pendiente", que es la
// mentira mas cara que puede decir un tablero de control.
// Referencia de PR de un evento: { repo, num }. SIN expresiones regulares a
// proposito -- este archivo se parchea con scripts y los escapes se pierden por
// el camino; buscar posiciones de texto no necesita escapes y no falla en mudo.
//
// POR QUE repo Y numero, y no solo el numero: los numeros de PR SE REPITEN entre
// repos. Medido sobre los eventos de 30 horas: 32 con numero, 30 con repo
// conocido, y ya hay 2 numeros que apuntan a mas de un origen. Emparejar por una
// llave que no es unica es como multiplicar filas en un JOIN: no da error, da
// una respuesta equivocada con cara de correcta.
function prRefOf(e) {
  let n = e?.notas;
  if (typeof n === 'string') { try { n = JSON.parse(n); } catch { n = null; } }
  const candidatos = [n?.pr_url, n?.prUrl, n?.pr, n?.pr_number, n?.prNumber];
  for (const c of candidatos) {
    if (typeof c !== 'string') continue;
    const i = c.lastIndexOf('/pull/');
    if (i < 0) continue;
    const num = parseInt(c.slice(i + 6), 10);
    if (!Number.isFinite(num)) continue;
    const partes = c.slice(0, i).split('/');
    return { repo: partes[partes.length - 1] || null, num };
  }
  for (const c of candidatos) {
    if (c == null || typeof c === 'string' && c.includes('/')) continue;
    const num = parseInt(String(c), 10);
    if (Number.isFinite(num)) return { repo: null, num };
  }
  const t = String(e?.titulo || '');
  const k = t.toUpperCase().indexOf('PR #');
  if (k >= 0) { const num = parseInt(t.slice(k + 4), 10); if (Number.isFinite(num)) return { repo: null, num }; }
  return null;
}
const claveDe = (ref) => (ref.repo || '?') + '#' + ref.num;
// Un DONE de ejecutor con PR y SIN dictamen posterior de Q = esperando compuerta.
function buildCompuerta(events, now = Date.now()) {
  const dictaminados = new Map();   // clave repo#num -> { ts, veredicto, ref }
  const entregas = new Map();       // clave repo#num -> { agentCode, titulo, ts, ref }
  let entregasHoySinPr = 0;
  const hoy = dayKeyBogota(now);
  for (const e of (events || [])) {
    const code = normalizeAgentCode(e.origen);
    const t = String(e.titulo || '');
    const ts = eventTsMs(e);
    const arriba = t.toUpperCase();
    if (code === 'ARGY-Q-LOCAL-001' && arriba.startsWith('[DICTAMEN]')) {
      const ref = prRefOf(e);
      if (!ref) continue;
      const k = claveDe(ref);
      const veredicto = arriba.includes('APROBAD') ? 'aprobado' : 'observado';
      if (!dictaminados.has(k) || ts > dictaminados.get(k).ts) dictaminados.set(k, { ts, veredicto, ref });
      continue;
    }
    if (!EXECUTOR_AGENTS.has(code)) continue;
    if (!arriba.startsWith('[DONE]')) continue;
    const ref = prRefOf(e);
    if (!ref) { if (dayKeyBogota(ts) === hoy) entregasHoySinPr++; continue; }
    const k = claveDe(ref);
    if (!entregas.has(k) || ts > entregas.get(k).ts) entregas.set(k, { agentCode: code, titulo: t, ts, ref });
  }
  // Un dictamen SIN repo (Q a veces cubre dos PRs en una sola entrada y ahi solo
  // queda el numero) solo cancela si ese numero apunta a UNA sola entrega. Si
  // hay dos, no se cancela ninguna: preferimos mostrar de mas -que el dueno
  // comprueba en un clic- a esconder algo que de verdad esta esperando.
  const porNumero = new Map();
  for (const [k, v] of entregas) {
    if (!porNumero.has(v.ref.num)) porNumero.set(v.ref.num, []);
    porNumero.get(v.ref.num).push(k);
  }
  const cancelado = (k, d) => {
    const dic = dictaminados.get(k);
    if (dic && dic.ts >= d.ts) return true;
    if (d.ref.repo) {
      const sinRepo = dictaminados.get('?#' + d.ref.num);
      if (sinRepo && sinRepo.ts >= d.ts && (porNumero.get(d.ref.num) || []).length === 1) return true;
    }
    return false;
  };
  const esperando = [];
  for (const [k, d] of entregas) if (!cancelado(k, d)) esperando.push({ clave: k, num: d.ref.num, repo: d.ref.repo, ...d });
  esperando.sort((a, b) => a.ts - b.ts);   // en ORDEN: el que mas lleva esperando, primero
  const dictaminadosHoy = [...dictaminados.values()]
    .filter(d => dayKeyBogota(d.ts) === hoy)
    .map(d => ({ num: d.ref.num, repo: d.ref.repo, veredicto: d.veredicto, ts: d.ts }))
    .sort((a, b) => b.ts - a.ts);
  return { esperando, dictaminadosHoy, entregasHoySinPr };
}

function buildReviewQueue(events, missionByCode) {
  const reviewedAt = new Map();
  for (const e of events) {
    if (normalizeAgentCode(e.origen) !== 'ARES-001') continue;
    if (!/^\[(REVIEW-OK|DONE)\]/i.test(e.titulo || '')) continue;
    const mc = missionCodeOfEvent(e);
    if (!mc) continue;
    const ts = eventTsMs(e);
    if (!reviewedAt.has(mc) || ts > reviewedAt.get(mc)) reviewedAt.set(mc, ts);
  }
  const seen = new Set();
  const queue = [];
  for (const e of events) { // events viene desc -> la primera por agente+mision es la mas reciente
    if (!EXECUTOR_AGENTS.has(normalizeAgentCode(e.origen))) continue;
    if (!/^\[DONE\]/i.test(e.titulo || '')) continue;
    const mc = missionCodeOfEvent(e);
    const key = normalizeAgentCode(e.origen) + '|' + (mc || e.titulo);
    if (seen.has(key)) continue;
    seen.add(key);
    if (mc && reviewedAt.has(mc) && reviewedAt.get(mc) > eventTsMs(e)) continue; // ya revisada
    queue.push({
      agentCode: normalizeAgentCode(e.origen),
      titulo: e.titulo,
      missionCode: mc || null,
      tsUtc: e.tsUtc || e.ts_utc,
      missionTitle: (mc && missionByCode.get(mc)) ? missionByCode.get(mc).title : null
    });
  }
  return queue.slice(0, 12);
}

// Avatares narrativos R&M (sub-tono Harry Potter en alt). DERIVADO del registro
// unico (PANEL-AGENTS-DASHBOARD-V1-001 B: drift kill) — antes era una 3ra lista
// hardcodeada que podia driftear de KNOWN_AGENTS/EXECUTOR_AGENTS.
const AGENT_PROFILE = Object.fromEntries(AGENT_REGISTRY.map(a => [a.code, {
  avatar: a.avatar, alias: a.alias, short: a.displayName, color: a.color
}]));

// T4 fix mapping canonico (PANEL-AGENTS-DASHBOARD-V1-001 #638):
//   WIP      -> verde   (#3fb950) trabajo en curso, animado pulse
//   DONE     -> azul    (#58a6ff) completado reciente
//   BLOCKED  -> AMARILLO (#d29922) requiere intervencion humana (Argy-d #631 lo necesitaba visible)
//   REDIRECT -> morado  (#bc8cff) cambio de scope, distinto de WIP
//   IDLE     -> gris    (#8b949e) sin actividad <30min
//   OFF      -> gris oscuro (#30363d) >24h sin actividad
const STATUS_META = {
  wip:      { label: 'WIP',      color: '#3fb950', dotShadow: 'rgba(63,185,80,0.5)' },
  done:     { label: 'DONE',     color: '#58a6ff', dotShadow: 'rgba(88,166,255,0.5)' },
  assigned: { label: 'ASSIGNED', color: '#e3b341', dotShadow: 'rgba(227,179,65,0.55)' },
  active:   { label: 'ACTIVO',   color: '#2ea043', dotShadow: 'rgba(46,160,67,0.45)' },
  blocked:  { label: 'BLOCKED',  color: '#d29922', dotShadow: 'rgba(210,153,34,0.5)' },
  redirect: { label: 'REDIRECT', color: '#bc8cff', dotShadow: 'rgba(188,140,255,0.5)' },
  idle:     { label: 'IDLE',     color: '#8b949e', dotShadow: 'rgba(139,148,158,0.4)' },
  off:      { label: 'OFF',      color: '#30363d', dotShadow: 'transparent' }
};

function formatRelativeTime(ms) {
  if (ms == null) return '—';
  const ageMs = Date.now() - ms;
  if (ageMs < 60_000) return 'ahora mismo';
  if (ageMs < 3_600_000) return `hace ${Math.floor(ageMs / 60_000)} min`;
  if (ageMs < 86_400_000) return `hace ${Math.floor(ageMs / 3_600_000)} h`;
  return `hace ${Math.floor(ageMs / 86_400_000)} d`;
}

export async function renderAgentsDashboard(container) {
  // Estado local
  const state = {
    agents: [],
    diario: null,
    decisiones: null,
    compuerta: null,
    reviewQueue: [],
    kpis: null,
    loading: true,
    error: null,
    selectedAgentCode: null,
    pollTimer: null,
    // Lo que se dejo desplegado: sobrevive al refresco de 30 s. Si no, el
    // widget se le cerraba en la cara a quien acababa de abrirlo.
    desplegados: new Set(),
    // Los origenes que NO son del equipo, para poder decir cuantos y cuales
    // en vez de esconderlos.
    noSonEquipo: []
  };

  container.innerHTML = `
    <section class="artifact-view agents-dashboard-view">
      <div class="artifact-header">
        <div>
          ${regresoHtml()}
          <h1>👥 Equipo Argys — Estado live</h1>
          <p style="font-size:0.875rem;color:#8b949e;margin-top:2px">Que esta pasando AHORA. Para la tendencia y la arquitectura: <a href="#/salud" style="color:#7ee7ff;text-decoration:none">&#9881;&#65039; Sala de Maquinas &rarr;</a></p>
          <p style="margin:4px 0 0;color:#8b949e;font-size:0.875rem">
            Refresca cada 30s. Click en una card para ver bitacora reciente.
          </p>
        </div>
        <div style="display:flex;align-items:center;gap:10px">
          <span data-aad-poll-status style="font-size:0.875rem;color:#8b949e">⟳ cargando…</span>
          <button data-aad-refresh class="btn-primary btn-sm" type="button">↻ Refrescar</button>
        </div>
      </div>

      <!-- SPEC KPI v1 (#2695): strip compacto de indicadores — una fila delgada que NO
           empuja el estado live (leccion ADS-T1). El detalle vive debajo de las cards. -->
      <div data-aad-kpi-strip style="display:flex;flex-wrap:wrap;gap:10px;margin-top:14px"></div>

      <div data-aad-grid class="agents-dashboard-grid"
           style="display:grid;grid-template-columns:repeat(auto-fill,minmax(260px,1fr));gap:14px;margin-top:14px">
      </div>

      <!-- EL ORDEN LO PIDIO JOSSE MIRANDO LA PANTALLA (PANEL-EQUIPO-ORDEN-T1,
           capturas del 10-sep 14:30): los widgets largos mandaban lo demas muy
           abajo. De arriba a abajo: los tiles de resumen, las cards de los
           Argys, la compuerta de Q, la cola de Ares, el avance cuantitativo, y
           al final los dos que crecen sin techo -- el diario y las decisiones.

           El criterio no es la importancia: es CUANTO CRECE cada uno. Un widget
           que hoy tiene tres filas y manana treinta no puede ir arriba, porque
           empuja lo que se mira siempre. -->
      <div data-aad-compuerta style="margin-top:18px"></div>
      <div data-aad-reviews style="margin-top:18px"></div>
      <div data-aad-kpi-detail style="margin-top:18px"></div>
      <div data-aad-diario style="margin-top:18px"></div>
      <div data-aad-decisiones style="margin-top:18px"></div>

      <section data-aad-drill class="agents-dashboard-drill" style="margin-top:18px;display:none">
        <div style="display:flex;align-items:center;justify-content:space-between;gap:8px;margin-bottom:10px">
          <h3 data-aad-drill-title style="margin:0;color:#f0f6fc;font-size:1rem"></h3>
          <button data-aad-drill-close class="btn-secondary btn-sm" type="button">Cerrar</button>
        </div>
        <div data-aad-drill-body></div>
      </section>
    </section>
  `;

  const $grid = container.querySelector('[data-aad-grid]');
  const $kpiStrip = container.querySelector('[data-aad-kpi-strip]');
  const $kpiDetail = container.querySelector('[data-aad-kpi-detail]');
  const $diario = container.querySelector('[data-aad-diario]');
  const $decisiones = container.querySelector('[data-aad-decisiones]');
  const $compuerta = container.querySelector('[data-aad-compuerta]');
  const $reviews = container.querySelector('[data-aad-reviews]');
  const $pollStatus = container.querySelector('[data-aad-poll-status]');
  const $refresh = container.querySelector('[data-aad-refresh]');
  const $drill = container.querySelector('[data-aad-drill]');
  const $drillTitle = container.querySelector('[data-aad-drill-title]');
  const $drillBody = container.querySelector('[data-aad-drill-body]');
  const $drillClose = container.querySelector('[data-aad-drill-close]');

  // Render skeleton cards mientras carga
  renderSkeleton();

  await loadAgents();
  startPolling();
  attachLifecycle();

  // ========== Funciones internas ==========

  function renderSkeleton() {
    const skeletons = KNOWN_AGENTS.map(code => {
      const profile = AGENT_PROFILE[code] || { avatar: '🤖', short: code, color: '#30363d' };
      return `
        <article class="aad-card aad-card-skeleton" data-aad-skeleton
                 style="background:#161b22;border:1px solid #30363d;border-radius:10px;padding:14px;opacity:0.6">
          <div style="display:flex;align-items:center;gap:10px">
            <div style="font-size:1.6rem">${profile.avatar}</div>
            <div style="flex:1;min-width:0">
              <div style="font-weight:600;color:#c9d1d9;font-size:0.92rem">${escHtml(profile.short)}</div>
              <div style="color:#8b949e;font-size:0.875rem;margin-top:2px">cargando…</div>
            </div>
          </div>
        </article>
      `;
    }).join('');
    $grid.innerHTML = skeletons;
  }

  async function loadAgents(forceKpiRefresh = false) {
    state.loading = true;
    $pollStatus.textContent = '⟳ cargando…';
    // v2.1 (#2742): misiones y tareas se traen COMPLETAS (paginacion real, el backend
    // capa pageSize a 50) con cache TTL — el poll de 30s solo refresca estado live
    // y bitacora; el dataset pesado se renueva cada 10 min o con ↻ Refrescar.
    const kpiStale = forceKpiRefresh || (Date.now() - _kpiData.ts) > KPI_DATA_TTL_MS;
    const [res, bitRes, nxsRes, missionsAll, tasksAll] = await Promise.all([
      AgentsDashboardApiClient.getAgentsState(100),
      // Se pide EL DIA (desde la medianoche de Bogota) con tope 500. Mientras la
      // API sea 1.6.44 esto devuelve las ultimas 100 igual que antes -- ignora
      // los dos parametros --, y cuando 1.6.45 este viva empieza a llegar el dia
      // entero sin tocar nada. El pie de la tira dice la ventana que de verdad
      // llego, asi que la pantalla no miente en ninguno de los dos casos.
      BitacoraApiClient.list(500, medianocheBogotaISO()).catch(() => ({ ok: false })),
      NxsApiClient.getBalances().catch(() => ({ ok: false })),
      kpiStale ? fetchAllPages(p => MissionApiClient.list(p, 50)) : Promise.resolve(null),
      kpiStale ? fetchAllPages(p => TaskApiClient.list(p, 50)) : Promise.resolve(null)
    ]);
    if (missionsAll) {
      _kpiData.missions = missionsAll.items;
      _kpiData.tasks = tasksAll ? tasksAll.items : [];
      _kpiData.partial = Boolean(missionsAll.partial || (tasksAll && tasksAll.partial));
      _kpiData.ts = Date.now();
    }
    state.loading = false;
    if (!res.ok) {
      state.error = res.error;
      $pollStatus.textContent = `⚠ error`;
      $grid.innerHTML = `<div class="empty-state" style="color:#f85149">Error: ${escHtml(JSON.stringify(res.error).slice(0, 120))}</div>`;
      return;
    }

    const missions = _kpiData.missions;
    const tasks = _kpiData.tasks;
    const events = pickItems(bitRes);

    // Indices: mision por code, status de tarea por id.
    const missionByCode = new Map();
    for (const m of missions) {
      const code = String(m.code || m.missionCode || m.mission_code || '').toUpperCase();
      if (code) missionByCode.set(code, m);
    }
    const taskStatusById = new Map();
    for (const t of tasks) taskStatusById.set(String(t.id), String(t.status || '').toLowerCase());
    // EQUIPO-T1: el MISMO dataset ya paginado, indexado por id para resolver la TAREA.
    const taskById = new Map();
    for (const t of tasks) taskById.set(String(t.id), t);

    // ADS-T5: excluir agentes legacy/retirados (ARGYS-001 pre-territorios) del dashboard live.
    //
    // Y ADEMAS LAS LLAVES DE SERVICIO (PANEL-EQUIPO-ORDEN-T1). Josse leyo
    // "6 agentes sin escribir en 7 dias" y los seis eran ARCADE-RELAY-001,
    // EMBUDO-BOT-001, MEMORY-COMPILER-N8N-001, CHATGPT-JOSSE-001 y los dos de
    // Argemiro. Ninguno es una persona que trabaje en misiones: son llaves de
    // servicio y clientes. La tabla nexo_agents tiene 13 filas; solo SEIS son
    // el equipo -- Ares y los cinco Argys --, y esa cuenta cuadra exactamente
    // con los seis que aparecian dormidos.
    //
    // NO SE ESCONDEN EN SILENCIO: se cuentan aparte y la pantalla dice cuantos
    // y por que. Un filtro mudo es la forma mas facil de que un dia falte
    // alguien de verdad y nadie lo note.
    const esDelEquipo = (codigo) => {
      const c = normalizeAgentCode(codigo);
      return c === 'ARES-001' || EXECUTOR_CODES.includes(c);
    };
    const fuera = res.agents.filter(a => !isLegacyAgent(a.agentCode) && !esDelEquipo(a.agentCode));
    state.noSonEquipo = fuera.map(a => normalizeAgentCode(a.agentCode) || a.agentCode);
    state.agents = res.agents
      .filter(a => !isLegacyAgent(a.agentCode) && esDelEquipo(a.agentCode))
      .map(a => enrichAgent(a, missionByCode, taskStatusById, events, taskById));
    state.reviewQueue = buildReviewQueue(events, missionByCode);
    state.compuerta = buildCompuerta(events);
    state.decisiones = buildDecisiones(tasks);
    state.diario = buildDiario(events, missionByCode);

    // SPEC KPI v1 (#2695) + v2.1 (#2742): indicadores con dataset COMPLETO paginado.
    const missionProgress = buildMissionProgress(missions, taskStatusById);
    state.kpis = {
      missionProgress,
      dataPartial: _kpiData.partial,
      dataAgeMs: Date.now() - _kpiData.ts,
      hitos: buildHitoProgress(missionByCode, taskStatusById),
      velocity: buildVelocity(events),
      nxs: buildNxsSummary(nxsRes, missionProgress),
      gates: buildGates(missionProgress, state.agents),
        pulso: buildPulso(events, missionByCode, buildGates(missionProgress, state.agents)),
      health: buildHealth(missions, taskStatusById, events)
    };

    state.error = null;
    renderKpiStrip();
    renderKpiDetail();
    renderReviewQueue();
    renderCompuerta();
    renderDecisiones();
    renderDiario();
    renderGrid();
    $pollStatus.textContent = `✓ actualizado ${formatearFecha(new Date(), {}, { parte: 'hora' })}`;
  }

  // EQUIPO-T5: los que llevan semanas quietos no ocupan el mismo espacio que los
  // que estan trabajando. Josse (2-sep): Argemiro figuraba "IDLE hace 151 d" y
  // Argy X "nunca", con la misma tarjeta grande que un agente activo. Medido el
  // 4-sep: de 16 origenes que han escrito alguna vez, 6 lo hicieron en los
  // ultimos 7 dias; los demas llevan de 15 a 154 dias. No se borra ninguno: se
  // pliegan en una fila que se abre con un clic.

  function renderGrid() {
    if (!state.agents.length) {
      $grid.innerHTML = `<div class="empty-state">Sin agentes</div>`;
      return;
    }
    const corte = Date.now() - DIAS_INACTIVO * 86400000;
    const activos = state.agents.filter((a) => a.lastTs && a.lastTs >= corte);
    const dormidos = state.agents.filter((a) => !(a.lastTs && a.lastTs >= corte));

    const cuerpo = activos.length
      ? activos.map((a) => renderCard(a)).join('')
      : `<div class="empty-state">Nadie ha escrito en la bitacora en ${DIAS_INACTIVO} dias</div>`;

    const plegados = dormidos.length
      ? `<div data-aad-dormidos-caja style="margin-top:12px">
           <button data-aad-dormidos-toggle
                   style="width:100%;text-align:left;background:#161b22;border:1px solid #30363d;border-radius:10px;padding:10px 14px;color:#8b949e;font-size:0.875rem;cursor:pointer">
             &#128164; ${dormidos.length} agente(s) sin escribir en ${DIAS_INACTIVO} dias &mdash; ${escHtml(dormidos.map((a) => a.name || a.agentCode).join(', '))}
             <span style="float:right;color:#6e7681">ver</span>
           </button>
           <div data-aad-dormidos-lista hidden style="margin-top:10px">${dormidos.map((a) => renderCard(a)).join('')}</div>
         </div>`
      : '';

    // Lo que se dejo fuera, con nombre y numero. Una linea, no un silencio.
    const aparte = (state.noSonEquipo && state.noSonEquipo.length)
      ? `<div style="margin-top:10px;font-size:0.875rem;color:#6e7681;line-height:1.4">
           Fuera del equipo: ${state.noSonEquipo.length} origen(es) que no son Argys ni Ares
           &mdash; ${escHtml(state.noSonEquipo.join(', '))}. Son llaves de servicio y clientes:
           escriben en la bitacora pero no ejecutan misiones, asi que no se cuentan como gente callada.
         </div>`
      : '';

    $grid.innerHTML = cuerpo + plegados + aparte;

    const $toggle = $grid.querySelector('[data-aad-dormidos-toggle]');
    const $lista = $grid.querySelector('[data-aad-dormidos-lista]');
    if ($toggle && $lista) {
      $toggle.addEventListener('click', () => {
        $lista.hidden = !$lista.hidden;
        $toggle.querySelector('span').textContent = $lista.hidden ? 'ver' : 'ocultar';
      });
    }

    // Bind click drilldown
    $grid.querySelectorAll('[data-aad-card-code]').forEach(card => {
      card.addEventListener('click', () => {
        const code = card.dataset.aadCardCode;
        openDrillDown(code);
      });
    });
  }

  // ===== SPEC KPI v1 (#2695): strip compacto (no empuja el estado live) =====
  function kpiChip({ icon, label, value, color, title }) {
    return `
      <div title="${escHtml(title || '')}"
           style="display:inline-flex;align-items:center;gap:8px;background:#161b22;border:1px solid #30363d;border-radius:10px;padding:8px 12px;min-width:0">
        <span style="font-size:1.05rem">${icon}</span>
        <div style="min-width:0">
          <div style="font-size:0.875rem;color:#8b949e;text-transform:uppercase;letter-spacing:.05em;white-space:nowrap">${escHtml(label)}</div>
          <div style="font-size:.88rem;font-weight:700;color:${color || '#f0f6fc'};white-space:nowrap">${value}</div>
        </div>
      </div>`;
  }

  function renderKpiStrip() {
    if (!$kpiStrip || !state.kpis) return;
    const k = state.kpis;
    const p = k.pulso;
    if (!p || !p.total) {
      $kpiStrip.innerHTML = '<div style="color:#8b949e;font-size:0.875rem">Sin anotaciones cargadas: no hay de donde sacar el pulso del equipo.</div>';
      return;
    }
    const ventana = p.horas >= 1 ? `${p.horas.toFixed(1)} h` : `${Math.round(p.horas * 60)} min`;
    // LA ETIQUETA DICE LO QUE DE VERDAD LLEGO, y por eso no envejece.
    //
    // Hasta el 4-sep decia "las ultimas 100 ... no es el dia completo", que era
    // cierto mientras la API topaba ahi. Desde 1.6.47 (BIT-PUB-T1) el since y el
    // limit funcionan: medido contra produccion, la peticion que hace esta vista
    // -- limit 500 desde la medianoche de Bogota -- devuelve 104 anotaciones y
    // 10,1 h, o sea el dia entero. Una advertencia que dejo de ser cierta
    // confunde igual que un dato falso (Ares, #10357).
    //
    // Asi que ya no se afirma un tope fijo: se compara lo que llego con lo que se
    // pidio. Si vinieron menos que el tope, es todo lo que hay desde la
    // medianoche; si vinieron justo el tope, es que se corto y se dice.
    const topo = p.total >= TOPE_BITACORA;
    const alcance = topo
      ? `Contado sobre las ultimas ${p.total} anotaciones, que abarcan ${ventana}. Se alcanzo el tope de la consulta, asi que hay mas dia del que se ve.`
      : `Contado sobre las ${p.total} anotaciones del dia (desde la medianoche), que abarcan ${ventana}.`;
    const gatesColor = p.gates === 0 ? '#3fb950' : '#bc8cff';
    $kpiStrip.innerHTML = [
      kpiChip({ icon: '&#129517;', label: 'En lo que anda el equipo', value: p.familia ? p.familia.nombre : 'sin familia declarada',
        color: '#58a6ff', title: p.familia ? `${p.familia.n} de ${p.total} anotaciones son de ${p.familia.code}. ${alcance}` : `Ninguna anotacion de la ventana declara mision. ${alcance}` }),
      kpiChip({ icon: '&#9989;', label: `Entregas (${ventana})`, value: String(p.entregas), color: '#3fb950',
        title: `Anotaciones [DONE] en la ventana. ${alcance}` }),
      kpiChip({ icon: '&#9878;&#65039;', label: `Dictamenes (${ventana})`, value: String(p.dictamenes), color: '#bc8cff',
        title: `Anotaciones [DICTAMEN] de Argy-Q en la ventana. ${alcance}` }),
      kpiChip({ icon: '&#128679;', label: 'Gates abiertos', value: p.gates == null ? '&mdash;' : String(p.gates), color: gatesColor,
        title: p.gatesDetalle || 'Misiones esperando validacion + bloqueos abiertos' }),
      kpiChip({ icon: '&#128273;', label: 'Sin decir que tarea', value: p.deuda && p.deuda.total ? `${p.deuda.sinLlave} de ${p.deuda.total}` : '&mdash;',
        color: p.deuda && p.deuda.sinLlave === 0 ? '#3fb950' : '#d29922',
        title: p.deuda && p.deuda.total
          ? `Anotaciones de trabajo ([WIP], [DONE], [BLOCKED]) que no declararon ninguna llave de tarea -- ni task_id, ni task_code, ni task_uuid -- sobre las ${p.deuda.total} de la ventana. Es deuda nuestra: sin esa llave, la tarjeta no puede decir en que anda el agente. ${alcance}`
          : `Ninguna anotacion de trabajo en la ventana. ${alcance}` }),
      kpiChip({ icon: '&#128101;', label: `Trabajando (${ventana})`, value: String(p.agentes), color: '#e3b341',
        title: `Agentes distintos que escribieron algo en la ventana. ${alcance}` })
    ].join('') + `<div style="flex-basis:100%;color:#6e7681;font-size:0.875rem;margin-top:2px">${escHtml(alcance)}</div>`;
  }

  // ===== SPEC KPI v1 (#2695): detalle cuantitativo debajo de las cards =====
  function kpiCard(icon, titleTxt, badge, bodyHtml) {
    return `
      <div style="background:#161b22;border:1px solid #30363d;border-radius:10px;padding:12px 14px;min-width:0">
        <div style="display:flex;align-items:center;gap:8px;margin-bottom:10px">
          <span style="font-size:1.05rem">${icon}</span>
          <strong style="color:#f0f6fc;font-size:.9rem;flex:1">${escHtml(titleTxt)}</strong>
          ${badge || ''}
        </div>
        ${bodyHtml}
      </div>`;
  }
  function kpiEmpty(txt) { return `<div style="color:#6e7681;font-size:0.875rem">${escHtml(txt)}</div>`; }

  function renderKpiDetail() {
    if (!$kpiDetail || !state.kpis) return;
    const k = state.kpis;

    // (b) hitos
    const hitosBody = k.hitos.hitos.length ? k.hitos.hitos.map(h => `
      <div style="display:flex;align-items:center;gap:8px;margin-bottom:7px"
           title="${h.missing.length ? escHtml(h.missing.length + ' mision(es) mapeada(s) sin datos: ' + h.missing.join(', ')) : ''}">
        <code style="width:26px;flex-shrink:0;color:#79c0ff;font-size:0.875rem;font-weight:700">${escHtml(h.hito)}</code>
        ${pctBar(h.pct)}
        <span style="width:40px;text-align:right;font-size:0.875rem;font-weight:700;color:${h.pct >= 100 ? '#3fb950' : '#c9d1d9'}">${h.pct}%</span>
        <span style="width:56px;text-align:right;font-size:0.875rem;color:#8b949e;white-space:nowrap">${h.nDone}/${h.nFound}✔${h.missing.length ? ' ⚠' : ''}</span>
      </div>`).join('') : kpiEmpty('Sin misiones mapeadas a hitos');
    const hitosCard = kpiCard('🗺️', 'Progreso por hito (ponderado NXS)',
      `<span style="background:#58a6ff22;color:#58a6ff;border-radius:10px;padding:1px 8px;font-size:0.875rem;font-weight:700">${k.hitos.globalPct}%</span>`,
      // EL PIE DICE DE DONDE SALE Y QUE DEJA FUERA (TABLERO-FIX-T1).
      //
      // Antes ponia "Mapeo v2 completo", y esa palabra -completo- era el
      // problema: la lista cubre 30 de 95 misiones activas y se llevaba por
      // delante el 68% de los NXS sin decirlo. Un pie que presume es donde hay
      // que buscar. Ahora el aviso se CALCULA sobre las misiones cargadas, asi
      // que si manana entran diez nuevas, crece solo.
      hitosBody + (k.hitos.fuente === 'base'
        ? `<div style="margin-top:6px;font-size:0.875rem;color:#6e7681">Hito declarado por cada mision. Las que no lo declaran salen como <em>(sin hito)</em>, no desaparecen.</div>`
        : `<div style="margin-top:6px;font-size:0.875rem;color:#d29922;line-height:1.5">&#9888;&#65039; Esto cuenta <strong>${k.hitos.fueraDeLaLista ? (k.hitos.fueraDeLaLista.activas - k.hitos.fueraDeLaLista.fuera) : '?'} de ${k.hitos.fueraDeLaLista ? k.hitos.fueraDeLaLista.activas : '?'}</strong> misiones activas: quedan fuera <strong>${k.hitos.fueraDeLaLista ? k.hitos.fueraDeLaLista.fuera : '?'}</strong>, el <strong>${k.hitos.fueraDeLaLista ? k.hitos.fueraDeLaLista.pctNxsFuera : '?'}%</strong> de los NXS en juego.<br>El hito todavia sale de una lista escrita a mano porque la API no manda <code>phaseCode</code>. Cuando lo mande, este numero se arregla solo.</div>`));

    // (a) misiones activas
    const TOP_MISSIONS = 10;
    const shown = k.missionProgress.slice(0, TOP_MISSIONS);
    const rest = k.missionProgress.length - shown.length;
    const misionesBody = shown.length ? shown.map(m => `
      <div style="display:flex;align-items:center;gap:8px;margin-bottom:7px" title="${escHtml(m.title)}">
        <code style="flex:0 0 46%;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;color:#79c0ff;font-size:0.875rem">${escHtml(m.code)}</code>
        ${pctBar(m.pct)}
        <span style="width:52px;text-align:right;font-size:0.875rem;color:#8b949e;white-space:nowrap">${m.done}/${m.total}</span>
        <span style="width:52px;text-align:right;font-size:0.875rem;color:#e3b341;white-space:nowrap">💎${Number(m.reward).toLocaleString('es-CO')}</span>
      </div>`).join('') + (rest > 0 ? `<div style="font-size:0.875rem;color:#6e7681;margin-top:4px">+${rest} mision(es) mas</div>` : '')
      : kpiEmpty('Sin misiones activas');
    const misionesCard = kpiCard('🎯', 'Progreso por mision activa',
      `<span style="background:#8b949e22;color:#8b949e;border-radius:10px;padding:1px 8px;font-size:0.875rem;font-weight:700">${k.missionProgress.length}</span>`,
      // LA SALVEDAD DE Q, ESCRITA (TABLERO-AUDIT-T1 #11585, veredicto: -si con
      // salvedad grande-). Estaba en su reporte y no en la pantalla, asi que
      // quien miraba el numero no la tenia.
      misionesBody + `<div style="margin-top:6px;font-size:0.875rem;color:#6e7681;line-height:1.5">Cuenta solo misiones <strong>abiertas</strong> (activas y en curso): las canceladas no entran, asi que el porcentaje no baja cuando se descarta trabajo.</div>`);

    // (c) velocidad
    const maxDay = Math.max(1, ...k.velocity.days.map(d => d.count));
    const velBars = `
      <div style="display:flex;align-items:flex-end;gap:6px;height:64px;margin-bottom:6px">
        ${k.velocity.days.map(d => `
          <div style="flex:1;display:flex;flex-direction:column;align-items:center;gap:3px;min-width:0">
            <span style="font-size:0.875rem;color:${d.count ? '#3fb950' : '#30363d'}">${d.count || ''}</span>
            <div style="width:100%;max-width:26px;height:${Math.round((d.count / maxDay) * 40)}px;min-height:2px;background:${d.count ? '#3fb950' : '#21262d'};border-radius:3px 3px 0 0"></div>
            <span style="font-size:0.875rem;color:#6e7681;white-space:nowrap">${escHtml(d.label)}</span>
          </div>`).join('')}
      </div>`;
    const velAgents = k.velocity.byAgent.length ? `
      <div style="display:flex;flex-wrap:wrap;gap:6px">
        ${k.velocity.byAgent.map(([code, n]) => {
          const p = AGENT_PROFILE[code] || { avatar: '🤖', short: code };
          return `<span style="font-size:0.875rem;color:#c9d1d9;background:#0d1117;border:1px solid #21262d;border-radius:10px;padding:2px 8px">${p.avatar} ${escHtml(p.short)} · ${n}</span>`;
        }).join('')}
      </div>` : '';
    const velCard = kpiCard('⚡', `Velocidad — DONEs por dia${k.velocity.partialWindow ? ` (ventana ${k.velocity.windowDays}d)` : ''}`,
      `<span style="background:#3fb95022;color:#3fb950;border-radius:10px;padding:1px 8px;font-size:0.875rem;font-weight:700">${k.velocity.total} · ${k.velocity.prCount} PRs</span>`,
      // LA SALVEDAD DE Q (veredicto: -si con salvedad conocida-): la ventana
      // depende de cuanta bitacora se alcanzo a cargar, no de los 7 dias.
      velBars + velAgents + `<div style="margin-top:6px;font-size:0.875rem;color:#6e7681;line-height:1.5">Se cuenta sobre la bitacora <strong>cargada</strong>, no sobre todo el historial: si la ventana sale corta, el titulo lo dice.</div>`);

    // (e) gates
    const gatesReady = k.gates.readyForValidation.length ? `
      <div style="font-size:0.875rem;color:#8b949e;margin-bottom:4px">Esperan validacion (tareas 100%):</div>
      ${k.gates.readyForValidation.map(m => `
        <div style="display:flex;align-items:center;gap:8px;font-size:0.875rem;color:#c9d1d9;background:#0d1117;border:1px solid #21262d;border-radius:6px;padding:5px 9px;margin-bottom:5px" title="${escHtml(m.title)}">
          <span>✅</span><code style="flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;color:#79c0ff;font-size:0.875rem">${escHtml(m.code)}</code>
          <span style="color:#8b949e;font-size:0.875rem">${m.done}/${m.total}</span>
          <span style="color:#e3b341;font-size:0.875rem">💎${Number(m.reward).toLocaleString('es-CO')}</span>
        </div>`).join('')}` : '';
    const gatesBlocked = k.gates.blockedOpen.length ? `
      <div style="font-size:0.875rem;color:#8b949e;margin:6px 0 4px">Bloqueos abiertos:</div>
      ${k.gates.blockedOpen.map(b => {
        const p = AGENT_PROFILE[b.agentCode] || { avatar: '🤖', short: b.agentCode };
        return `<div style="display:flex;align-items:center;gap:8px;font-size:0.875rem;color:#d29922;background:#d2992212;border:1px solid #d2992244;border-radius:6px;padding:5px 9px;margin-bottom:5px">
          <span>⛔</span><span>${p.avatar} ${escHtml(p.short)}</span>
          ${b.missionCode ? `<code style="flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-size:0.875rem">${escHtml(b.missionCode)}</code>` : ''}
        </div>`;
      }).join('')}` : '';
    const gatesCard = kpiCard('🚧', 'Gates y cuellos de botella',
      `<span style="background:${k.gates.count ? '#bc8cff22' : '#3fb95022'};color:${k.gates.count ? '#bc8cff' : '#3fb950'};border-radius:10px;padding:1px 8px;font-size:0.875rem;font-weight:700">${k.gates.count}</span>`,
      (gatesReady + gatesBlocked) || kpiEmpty('Sin cuellos de botella 🎉'));

    // (f) salud
    const healthRows = [];
    if (k.health.stalled.length) healthRows.push(`
      <div style="font-size:0.875rem;color:#8b949e;margin-bottom:4px">Estancadas &gt;30d:</div>
      ${k.health.stalled.map(m => `<div style="font-size:0.875rem;color:#f85149;margin-bottom:3px" title="${escHtml(m.title)}">🐌 <code style="font-size:0.875rem">${escHtml(m.code)}</code></div>`).join('')}`);
    if (k.health.orphanCount > 0) healthRows.push(`
      <div style="font-size:0.875rem;color:#d29922;margin-bottom:3px" title="Tareas pending/in_progress en misiones completed (ej: ${escHtml(k.health.orphanSample.join(', '))})">
        👻 ${k.health.orphanCount} tarea(s) huerfana(s) en misiones cerradas</div>`);
    if (k.health.wipStale.length) healthRows.push(k.health.wipStale.map(w => {
      const p = AGENT_PROFILE[w.agentCode] || { avatar: '🤖', short: w.agentCode };
      return `<div style="font-size:0.875rem;color:#d29922;margin-bottom:3px">⏳ WIP sin cierre ${escHtml(formatRelativeTime(Date.now() - w.ageMs))}: ${p.avatar} ${escHtml(p.short)} · <code style="font-size:0.875rem">${escHtml(w.missionCode)}</code></div>`;
    }).join(''));
    const healthCard = kpiCard('🩺', 'Salud del sistema',
      `<span style="background:${k.health.alertCount ? '#d2992222' : '#3fb95022'};color:${k.health.alertCount ? '#d29922' : '#3fb950'};border-radius:10px;padding:1px 8px;font-size:0.875rem;font-weight:700">${k.health.alertCount ? k.health.alertCount + ' ⚠' : 'OK'}</span>`,
      healthRows.length ? healthRows.join('') : kpiEmpty('Todo sano: sin estancadas, sin huerfanas, sin WIP viejos'));

    $kpiDetail.innerHTML = `
      <div style="display:flex;align-items:center;gap:8px;margin-bottom:10px">
        <span style="font-size:1.1rem">📊</span>
        <strong style="color:#f0f6fc;font-size:.95rem">Avance cuantitativo</strong>
        <span style="font-size:0.875rem;color:#6e7681">misiones y tareas completas (paginado BD, corte hace ${Math.max(0, Math.round((k.dataAgeMs || 0) / 60000))} min · ↻ fuerza recarga) + bitacora + NXS live${k.dataPartial ? ' · <b style="color:#d29922">⚠ dataset parcial</b>' : ''}</span>
      </div>
      <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(320px,1fr));gap:12px">
        ${hitosCard}${misionesCard}${velCard}${gatesCard}${healthCard}
      </div>`;
  }

  // #2465 — vista Ares: cola de entregas de Argys esperando review.
  // EQUIPO-T2 -- AUDIENCIA (regla #5638): Josse en la Sala de Maquinas, para ver
  // que esta atascado esperando el visto bueno de Q y que ya paso hoy.
  // EQUIPO-T3 -- AUDIENCIA (regla #5638): Josse en la Sala de Maquinas, la vista
  // donde decide. Cada fila: la pregunta en su idioma y que se destraba.
  // EQUIPO-T4 -- AUDIENCIA (regla #5638): Josse. Su pedido del 2-sep, en pantalla.
  function renderDiario() {
    if (!$diario) return;
    const d = state.diario;
    if (!d || !d.lineas) { $diario.innerHTML = ''; return; }
    const fila = (f) => `<div style="display:flex;gap:8px;font-size:0.875rem;color:#c9d1d9;line-height:1.5;padding:2px 0">
      <span style="color:#6e7681;flex-shrink:0;font-variant-numeric:tabular-nums">${escHtml(f.hora)}</span>
      <span style="color:${f.color};flex-shrink:0;min-width:96px">${escHtml(f.quien)} ${escHtml(f.verbo)}</span>
      <span style="flex:1;min-width:0">${escHtml(f.que)}</span>
    </div>`;
    const grupo = (g) => `<div style="margin-top:10px">
      <div style="font-size:0.875rem;color:${g.esSinFamilia ? '#d29922' : '#6e7681'};text-transform:uppercase;letter-spacing:.04em;margin-bottom:3px">
        ${escHtml(g.nombre)} &middot; ${g.filas.length}
      </div>
      ${g.esSinFamilia ? `<div style="font-size:0.875rem;color:#8b949e;margin-bottom:4px;line-height:1.4">
        Estas no dijeron a que familia pertenecen. No las escondo: si no aparecieran, el diario se veria completo sin serlo.
      </div>` : ''}
      ${plegarFilas(g.filas, fila, 'diario:' + g.nombre, state.desplegados)}
    </div>`;
    $diario.innerHTML = `
      <div style="background:#161b22;border:1px solid #30363d;border-left:3px solid #3fb950;border-radius:10px;padding:12px 14px">
        <div style="display:flex;align-items:center;gap:8px;margin-bottom:2px">
          <span style="font-size:1.1rem">\u{1F4D3}</span>
          <strong style="color:#f0f6fc;font-size:.92rem">Diario de hoy</strong>
          <span style="background:#3fb95022;color:#3fb950;border-radius:10px;padding:1px 8px;font-size:0.875rem;font-weight:600">${d.lineas}</span>
        </div>
        <div style="font-size:0.875rem;color:#6e7681;line-height:1.4">Lo que paso hoy, una linea por cosa. Sin abrir la bitacora.</div>
        ${d.grupos.map(grupo).join('')}
        <div style="margin-top:10px;padding-top:8px;border-top:1px solid #21262d;font-size:0.875rem;color:${d.leidas >= TOPE_BITACORA ? '#d29922' : '#6e7681'};line-height:1.4">
          Armado con ${d.leidas} anotaciones${d.masViejo ? `, desde las ${escHtml(formatearFecha(d.masViejo, { timeZone: 'America/Bogota', hour: '2-digit', minute: '2-digit' }, { parte: 'hora' }))}` : ''}.
          ${d.leidas >= TOPE_BITACORA
            ? 'Se alcanzo el tope de la consulta, asi que lo de antes de esa hora no esta aqui.'
            : 'Es el dia completo: la consulta trae todo desde la medianoche.'}
        </div>
      </div>`;
  }

  function renderDecisiones() {
    if (!$decisiones) return;
    const d = state.decisiones;
    if (!d || !d.filas.length) { $decisiones.innerHTML = ''; return; }
    const pendientes = d.filas.filter(f => !f.entregada).length;
    const fila = (f) => {
      const col = f.entregada ? '#6e7681' : '#e3b341';
      const cuando = f.tocadaMs ? formatRelativeTime(f.tocadaMs) : null;
      return `<div style="background:#0d1117;border:1px solid #21262d;border-left:3px solid ${col};border-radius:6px;padding:8px 10px">
        <div style="font-size:0.875rem;color:#c9d1d9;line-height:1.4">${escHtml(f.pregunta)}</div>
        <div style="margin-top:5px;font-size:0.875rem;color:#6e7681;line-height:1.35">
          ${f.entregada
            ? 'La tarea ya se entrego, pero esto sigue sin respuesta: '
            : 'Destraba: '}<span style="color:#8b949e">${escHtml(f.tarea)}</span>
          ${cuando ? ` &middot; sin moverse desde hace ${escHtml(cuando)}` : ''}
        </div>
      </div>`;
    };
    $decisiones.innerHTML = `
      <div style="background:#161b22;border:1px solid #30363d;border-left:3px solid #e3b341;border-radius:10px;padding:12px 14px">
        <div style="display:flex;align-items:center;gap:8px;margin-bottom:4px">
          <span style="font-size:1.1rem">\u{1F5F3}</span>
          <strong style="color:#f0f6fc;font-size:.92rem">Decisiones que solo tomas vos</strong>
          <span style="background:#e3b34122;color:#e3b341;border-radius:10px;padding:1px 8px;font-size:0.875rem;font-weight:600">${pendientes}</span>
        </div>
        <div style="font-size:0.875rem;color:#6e7681;margin-bottom:9px;line-height:1.4">
          Nadie mas puede resolverlas. Las de arriba frenan una tarea viva; las grises ya no frenan nada, pero siguen sin respuesta.
        </div>
        <div style="display:flex;flex-direction:column;gap:6px">${plegarFilas(d.filas, fila, 'decisiones', state.desplegados)}</div>
        ${d.conClaveVacia ? `
          <div style="margin-top:9px;font-size:0.875rem;color:#6e7681;line-height:1.4">
            Otras ${d.conClaveVacia} tareas dicen expresamente que no necesitan decision tuya. No se listan.
          </div>` : ''}
        <div style="margin-top:9px;padding-top:8px;border-top:1px solid #21262d;font-size:0.875rem;color:#d29922;line-height:1.4">
          Falta traer aqui las 19 decisiones del plan de la plataforma de contexto: hoy viven en un archivo del repositorio
          y no en la base, asi que el panel todavia no las puede leer. Cuando la API las publique, aparecen solas.
        </div>
      </div>`;
  }

  function renderCompuerta() {
    if (!$compuerta) return;
    const c = state.compuerta;
    if (!c) { $compuerta.innerHTML = ''; return; }
    const { esperando, dictaminadosHoy, entregasHoySinPr } = c;
    if (!esperando.length && !dictaminadosHoy.length && !entregasHoySinPr) { $compuerta.innerHTML = ''; return; }
    const fila = (r) => {
      const p = AGENT_PROFILE[r.agentCode] || { avatar: '\u{1F916}', short: r.agentCode };
      const espera = formatRelativeTime(r.ts);
      return `<div style="display:flex;align-items:center;gap:8px;font-size:0.875rem;color:#c9d1d9;background:#0d1117;border:1px solid #21262d;border-radius:6px;padding:6px 10px">
        <span>${p.avatar}</span>
        <span style="color:#8b949e">${escHtml(p.short)}</span>
        <code style="font-size:0.875rem;color:#79c0ff" title="${escHtml(r.repo || 'repo no declarado')}">#${r.num}</code>
        <span style="flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${escHtml(naturalizeEvent(r.titulo))}</span>
        <span style="color:#6e7681;font-size:0.875rem;white-space:nowrap">espera ${escHtml(espera)}</span>
      </div>`;
    };
    const chip = (d) => {
      const ok = d.veredicto === 'aprobado';
      const col = ok ? '#3fb950' : '#d29922';
      return `<span style="font-size:0.875rem;color:${col};background:${col}18;border:1px solid ${col}44;border-radius:10px;padding:2px 8px;white-space:nowrap" title="${escHtml(d.repo || 'repo no declarado')}">#${d.num} ${ok ? 'aprobado' : 'con observacion'}</span>`;
    };
    $compuerta.innerHTML = `
      <div style="background:#161b22;border:1px solid #30363d;border-left:3px solid #58a6ff;border-radius:10px;padding:12px 14px">
        <div style="display:flex;align-items:center;gap:8px;margin-bottom:8px">
          <span style="font-size:1.1rem">\u{1F6A6}</span>
          <strong style="color:#f0f6fc;font-size:.92rem">Compuerta: esperando el visto bueno de Q</strong>
          <span style="background:#58a6ff22;color:#58a6ff;border-radius:10px;padding:1px 8px;font-size:0.875rem;font-weight:600">${esperando.length}</span>
        </div>
        ${esperando.length
          ? `<div style="display:flex;flex-direction:column;gap:6px">${esperando.map(fila).join('')}</div>`
          : `<div style="font-size:0.875rem;color:#8b949e">Nada esperando dictamen ahora mismo.</div>`}
        ${dictaminadosHoy.length ? `
          <div style="margin-top:10px;padding-top:9px;border-top:1px solid #21262d">
            <div style="font-size:0.875rem;color:#6e7681;text-transform:uppercase;letter-spacing:.04em;margin-bottom:5px">Dictaminados hoy</div>
            <div style="display:flex;gap:6px;flex-wrap:wrap">${dictaminadosHoy.map(chip).join('')}</div>
          </div>` : ''}
        ${entregasHoySinPr ? `
          <div style="margin-top:9px;font-size:0.875rem;color:#d29922;line-height:1.4">
            Ojo: hoy hay ${entregasHoySinPr} entrega(s) que no declararon su PR, asi que esta lista puede quedarse corta.
            No es que no haya nada pendiente: es que de esas no se puede saber.
          </div>` : ''}
      </div>`;
  }

  function renderReviewQueue() {
    if (!$reviews) return;
    const q = state.reviewQueue || [];
    if (!q.length) { $reviews.innerHTML = ''; return; }
    $reviews.innerHTML = `
      <div style="background:#161b22;border:1px solid #30363d;border-left:3px solid #bc8cff;border-radius:10px;padding:12px 14px">
        <div style="display:flex;align-items:center;gap:8px;margin-bottom:8px">
          <span style="font-size:1.1rem">🧑‍⚖️</span>
          <strong style="color:#f0f6fc;font-size:.92rem">Cola de reviews de Ares</strong>
          <span style="background:#bc8cff22;color:#bc8cff;border-radius:10px;padding:1px 8px;font-size:0.875rem;font-weight:600">${q.length}</span>
        </div>
        <div style="display:flex;flex-direction:column;gap:6px">
          ${q.map(r => {
            const p = AGENT_PROFILE[r.agentCode] || { avatar: '🤖', short: r.agentCode };
            return `
              <div style="display:flex;align-items:center;gap:8px;font-size:0.875rem;color:#c9d1d9;background:#0d1117;border:1px solid #21262d;border-radius:6px;padding:6px 10px">
                <span>${p.avatar}</span>
                <span style="color:#8b949e">${escHtml(p.short)}</span>
                <span style="flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${escHtml(naturalizeEvent(r.titulo))}</span>
                ${r.missionCode ? `<code style="font-size:0.875rem;color:#79c0ff">${escHtml(r.missionCode)}</code>` : ''}
                <span style="color:#6e7681;font-size:0.875rem;white-space:nowrap">${escHtml(r.tsUtc ? formatRelativeTime(new Date(r.tsUtc).getTime()) : '')}</span>
              </div>`;
          }).join('')}
        </div>
      </div>`;
  }

  function renderCard(agent) {
    const profile = AGENT_PROFILE[agent.agentCode] || { avatar: '🤖', short: agent.agentCode, color: '#30363d', alias: '' };
    // Se pinta lo que dice comoVa, que sale de la misma regla que el Gremio.
    // STATUS_META queda de respaldo por si un agente llega sin eventos.
    const meta = agent.comoVa || STATUS_META[agent.status] || STATUS_META.off;
    const heartbeat = agent.lastTs ? formatRelativeTime(agent.lastTs) : 'nunca';
    const isSelected = state.selectedAgentCode === agent.agentCode;
    // Late el que esta metido en algo, no el que tiene una marca llamada wip.
    const animate = agent.comoVa?.clave === 'trabajando' ? 'animation: aad-pulse 1.5s ease-in-out infinite;' : '';
    // #2495 frente2: edad del ultimo evento por umbral (verde <10min, gris <30min, ambar <12h, rojo).
    const ageMs = agent.lastTs ? Date.now() - agent.lastTs : Infinity;
    const ageColor = ageMs < 10 * 60_000 ? '#3fb950' : ageMs < 30 * 60_000 ? '#8b949e' : ageMs < 12 * 3_600_000 ? '#d29922' : '#f85149';

    return `
      <article class="aad-card"
               data-aad-card-code="${escHtml(agent.agentCode)}"
               data-aad-status="${escHtml(agent.comoVa?.clave || agent.status)}"
               title="${escHtml(agent.comoVa?.frase || '')}"
               style="background:${isSelected ? '#1c2530' : '#161b22'};border:1px solid ${isSelected ? meta.color : '#30363d'};
                      border-left:3px solid ${profile.color};border-radius:10px;padding:14px;cursor:pointer;
                      transition:border-color 0.15s, transform 0.15s;"
               title="${escHtml(profile.alias)}">
        <div style="display:flex;align-items:flex-start;gap:10px">
          <div style="font-size:1.8rem;line-height:1">${profile.avatar}</div>
          <div style="flex:1;min-width:0">
            <div style="display:flex;align-items:center;justify-content:space-between;gap:6px">
              <strong style="color:#c9d1d9;font-size:0.95rem">${escHtml(profile.short)}</strong>
              <span style="display:inline-flex;align-items:center;gap:4px;font-size:0.875rem;color:${meta.color};font-weight:600;letter-spacing:0.04em;text-transform:uppercase">
                <span style="width:8px;height:8px;border-radius:50%;background:${meta.color};box-shadow:0 0 6px ${meta.dotShadow};${animate}"></span>
                ${escHtml(meta.label)}
              </span>
            </div>
            <code style="display:block;font-size:0.875rem;color:#6e7681;margin-top:2px">${escHtml(agent.agentCode)}</code>
          </div>
        </div>
        <div style="margin-top:8px;display:flex;gap:6px;flex-wrap:wrap">
          ${agent.vida ? `<span title="${escHtml(agent.vida.detalle)}" style="display:inline-flex;align-items:center;gap:5px;font-size:0.875rem;font-weight:600;color:${agent.vida.color};background:${agent.vida.color}18;border:1px solid ${agent.vida.color}44;border-radius:10px;padding:2px 8px">
            <span style="width:7px;height:7px;border-radius:50%;background:${agent.vida.color}${agent.vida.clave === 'vivo' ? ';box-shadow:0 0 6px ' + agent.vida.color + '88;animation:aad-pulse 1.2s ease-in-out infinite' : ''}"></span>${escHtml(agent.vida.etiqueta)}</span>` : ''}
          ${agent.blockedOpen ? `<span title="Bloqueo ABIERTO: [BLOCKED] sin [UNBLOCK]/[DONE] de esa mision. No lo tapa un DONE de otra mision." style="font-size:0.875rem;font-weight:700;color:#d29922;background:#d2992218;border:1px solid #d2992277;border-radius:10px;padding:2px 8px">⛔ BLOQUEO ABIERTO</span>` : ''}
          ${agent.assignedCold ? `<span title="Asignado hace mas de 24h sin arrancar (sin [WIP] posterior)" style="font-size:0.875rem;font-weight:600;color:#8b949e;background:#8b949e18;border:1px solid #8b949e44;border-radius:10px;padding:2px 8px">❄️ asignado sin arrancar</span>` : ''}
          ${agent.sinTrabajo ? `<span title="Sin mision asignada — Ares puede darle trabajo" style="font-size:0.875rem;font-weight:600;color:#d29922;background:#d2992218;border:1px solid #d2992244;border-radius:10px;padding:2px 8px">⚠ sin tarea</span>` : ''}
        </div>
        <div style="margin-top:10px;font-size:0.875rem;color:#8b949e">
          <span style="color:${ageColor}" title="Edad del ultimo evento">🕐 ${escHtml(heartbeat)}</span>
          ${agent.missionCode ? `<span style="color:#30363d;margin:0 5px">·</span>📋 <code style="font-size:0.875rem;color:#79c0ff">${escHtml(agent.missionCode)}</code>` : ''}
        </div>
        ${(() => {
          // EQUIPO-T1 (regla #5638 -- AUDIENCIA: Josse en la Sala de Maquinas).
          // Lo primero que se lee es QUE HACE el Argy, no a que mision pertenece.
          //
          // POR QUE NO HAY BARRA DE PASOS AQUI, y no es un olvido: medido el 3-sep
          // sobre los 5 Argys vivos, NINGUNO tenia un paso marcado EN LA TAREA QUE
          // LA TARJETA MUESTRA -hay 3 con pasos marcados en tareas viejas, pero
          // ninguna de esas es la actual (matiz medido por Argy-Q, #9910)-, y en
          // toda la base solo 19 de 1.643 tareas tienen alguno. Pintar "paso 0 de 6"
          // NO diria "no ha avanzado": diria "nadie marco", y el dueno leeria lo
          // primero. Un indicador que no es confiable NO se muestra. El dia que los
          // pasos se marquen de verdad, este es el sitio donde va la barra.
          const ct = agent.currentTask;
          if (!ct) return "";
          if (ct.reason) {
            // Si al menos se sabe la FAMILIA, se dice: es menos que la tarea,
            // pero mucho mas que "no se puede saber".
            return ct.familia
              ? `<div style="margin-top:8px;font-size:0.875rem;color:#6e7681;line-height:1.35"><span style="color:#8b949e">Anda en:</span> ${escHtml(ct.familia)} <span style="color:#484f58">(${escHtml(ct.reason)})</span>.</div>`
              : `<div style="margin-top:8px;font-size:0.875rem;color:#6e7681;line-height:1.35"><span style="color:#8b949e">Tarea:</span> no se puede saber todavia -- ${escHtml(ct.reason)}.</div>`;
          }
          const doneTask = ct.status === 'completed';
          const acc = doneTask ? '#3fb950' : '#58a6ff';
          return `<div style="margin-top:8px;padding:8px 10px;background:#0d1117;border:1px solid #30363d;border-left:3px solid ${acc};border-radius:6px"><div style="font-size:0.875rem;color:#6e7681;text-transform:uppercase;letter-spacing:.04em;margin-bottom:3px">Tarea ahora</div><div style="font-size:0.875rem;color:#c9d1d9;font-weight:600;line-height:1.35">${escHtml(ct.title)}</div></div>`;
        })()}
        ${agent.missionCode ? `
          <div style="margin-top:8px;display:flex;align-items:flex-start;gap:8px">
            <div style="flex:1;min-width:0;font-size:0.875rem;color:#c9d1d9;line-height:1.35">${escHtml(agent.missionTitle || 'Mision activa')}</div>
            ${agent.reward != null ? `<span style="flex-shrink:0;background:#e3b34122;color:#e3b341;border-radius:6px;padding:2px 7px;font-size:0.875rem;font-weight:700;white-space:nowrap">💎 ${escHtml(Number(agent.reward).toLocaleString('es-CO'))}</span>` : ''}
          </div>
          ${agent.tasksTotal > 0 ? tasksBar(agent.tasksDone, agent.tasksTotal) : ''}
        ` : ''}
        ${agent.lastNatural ? `<p style="margin:8px 0 0;color:#8b949e;font-size:0.875rem;line-height:1.4">${escHtml(agent.lastNatural)}</p>` : ''}
        ${(agent.streak || agent.eventCount) ? `
          <div style="margin-top:8px;display:flex;gap:12px;font-size:0.875rem;color:#6e7681">
            ${agent.streak ? `<span title="Entregas [DONE] recientes">🔥 ${agent.streak} entrega(s)</span>` : ''}
            ${agent.eventCount ? `<span>${agent.eventCount} evento(s)</span>` : ''}
          </div>` : ''}
      </article>
    `;
  }

  function openDrillDown(code) {
    const agent = state.agents.find(a => a.agentCode === code);
    if (!agent) return;
    state.selectedAgentCode = code;
    renderGrid();

    const profile = AGENT_PROFILE[code] || { avatar: '🤖', short: code };
    $drill.style.display = 'block';
    $drillTitle.innerHTML = `${profile.avatar} ${escHtml(profile.short)} — Bitacora reciente`;

    if (!agent.recentEvents.length) {
      $drillBody.innerHTML = `<div class="empty-state" style="padding:20px">Sin eventos en bitacora</div>`;
      return;
    }

    $drillBody.innerHTML = `
      <div style="display:flex;flex-direction:column;gap:8px">
        ${agent.recentEvents.map(ev => {
          const ts = ev.tsUtc || ev.ts_utc;
          const tsLabel = ts ? formatearFecha(ts, { dateStyle: 'short', timeStyle: 'short' }, { parte: 'todo' }) : '';
          return `
            <article style="background:#0d1117;border:1px solid #21262d;border-radius:6px;padding:10px 12px">
              <div style="display:flex;justify-content:space-between;gap:8px;font-size:0.875rem;color:#8b949e">
                <code>${escHtml(ev.origen || '')}</code>
                <span>${escHtml(tsLabel)}</span>
              </div>
              <div style="margin-top:4px;color:#c9d1d9;font-size:0.875rem;line-height:1.4">
                ${escHtml(ev.titulo || '')}
              </div>
            </article>
          `;
        }).join('')}
      </div>
    `;

    // Scroll suave
    $drill.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  function startPolling() {
    if (state.pollTimer) clearInterval(state.pollTimer);
    state.pollTimer = setInterval(() => {
      loadAgents();
    }, POLL_INTERVAL_MS);
  }

  function attachLifecycle() {
    // v2.1: el refresh manual fuerza recarga COMPLETA del dataset paginado (salta el TTL).
    // UN SOLO OYENTE PARA TODOS LOS "ver mas", delegado en el contenedor.
    // Los widgets se repintan cada 30 s; enganchar boton por boton en cada
    // repintado deja oyentes colgados y termina disparando dos veces.
    container.addEventListener('click', (ev) => {
      const b = ev.target && ev.target.closest && ev.target.closest('[data-aad-vermas]');
      if (!b || !container.contains(b)) return;
      const k = b.getAttribute('data-aad-vermas');
      if (state.desplegados.has(k)) state.desplegados.delete(k); else state.desplegados.add(k);
      renderDiario();
      renderDecisiones();
    });

    $refresh.addEventListener('click', () => loadAgents(true));
    $drillClose.addEventListener('click', () => {
      $drill.style.display = 'none';
      state.selectedAgentCode = null;
      renderGrid();
    });

    // Cleanup polling cuando la vista se destruye
    const observer = new MutationObserver(() => {
      if (!container.isConnected) {
        if (state.pollTimer) clearInterval(state.pollTimer);
        observer.disconnect();
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });

    // Inyectar @keyframes pulse una sola vez
    if (!document.getElementById('aad-pulse-style')) {
      const style = document.createElement('style');
      style.id = 'aad-pulse-style';
      style.textContent = '@keyframes aad-pulse{0%,100%{opacity:1;transform:scale(1)}50%{opacity:0.55;transform:scale(0.85)}}';
      document.head.appendChild(style);
    }
  }
}
